import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  createAiChatSession,
  createChatSession,
  deleteAiChatSession,
  deleteAiChatTurnMessages,
  getAvailableAiModels,
  getAiChatMessages,
  getAiChatSessions,
  getChatMessages,
  getDirectMessageSessions,
  getFriendships,
  getProfilesLastOnline,
  getPracticeLanguages,
  getPublicSetting,
  getTutorStudentChatMessages,
  getTutorStudentChatSessions,
  getTutorStudentUnreadCountsBySessions,
  getUnreadCountsBySessions,
  markChatSessionRead,
  sendChatMessage,
  startAiChatSession,
  updateAiChatSessionName,
} from '../api/supabaseAPI';
import ConfirmDialog from './ConfirmDialog';
import ModernCombobox from './ModernCombobox';
import ToastStack from './ToastStack';
import TypingIndicator from './TypingIndicator';
import { supabase } from '../supabaseClient';
import '../styles/ChatHubPanel.css';

const COMPOSER_MAX_LINES = 4;
const TYPING_STOP_DELAY_MS = 1200;
const MESSAGE_CLIENT_FALLBACK_KEEP_MS = 10000;
const READ_MARK_FLUSH_DELAY_MS = 1500;
const CHAT_PANEL_REFRESH_INTERVAL_MS = 5000;
const REALTIME_INSERT_DEDUPE_KEEP_MS = 15000;
const AI_SOCKET_READY_TIMEOUT_MS = 12000;
const AI_STREAM_RENDER_TICK_MS = 34;
const AI_STREAM_MIN_CHARS_PER_SECOND = 18;
const AI_STREAM_MAX_CHARS_PER_SECOND = 120;
const AI_STREAM_BACKLOG_BOOST_THRESHOLD = 120;
const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const isSameCalendarDay = (left, right) => (
  left.getFullYear() === right.getFullYear()
  && left.getMonth() === right.getMonth()
  && left.getDate() === right.getDate()
);

const formatMessageDateDivider = (timestamp) => {
  if (!timestamp) return '';

  const targetDate = new Date(timestamp);
  if (Number.isNaN(targetDate.getTime())) return '';

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(todayStart.getDate() - 1);
  const targetStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());

  if (isSameCalendarDay(targetStart, todayStart)) {
    return 'Today';
  }

  if (isSameCalendarDay(targetStart, yesterdayStart)) {
    return 'Yesterday';
  }

  if (targetDate.getFullYear() === now.getFullYear()) {
    return targetDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
    });
  }

  return targetDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const markdownComponents = {
  a: ({ ...props }) => <a {...props} target="_blank" rel="noreferrer" />,
};

const makeLocalId = () => `local-${Math.random().toString(36).slice(2, 10)}`;
const REALTIME_GATEWAY_WS_BASE = (import.meta.env.VITE_REALTIME_GATEWAY_WS_BASE || 'wss://hk0712-theralingua-realtime-gateway.hf.space/ws').trim();
const DEFAULT_AVATAR_URL = '/Resources/imagine/default_avatar/default_avatar.jpeg';

const isExternalUrl = (value) => /^https?:\/\//i.test(value || '');

const getInitialChar = (name) => (name || '?').trim().charAt(0).toUpperCase() || '?';

const TrashBinIcon = () => (
  <svg data-testid="geist-icon" height="16" strokeLinejoin="round" viewBox="0 0 16 16" width="16" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.75 2.75C6.75 2.05964 7.30964 1.5 8 1.5C8.69036 1.5 9.25 2.05964 9.25 2.75V3H6.75V2.75ZM5.25 3V2.75C5.25 1.23122 6.48122 0 8 0C9.51878 0 10.75 1.23122 10.75 2.75V3H12.9201H14.25H15V4.5H14.25H13.8846L13.1776 13.6917C13.0774 14.9942 11.9913 16 10.6849 16H5.31508C4.00874 16 2.92263 14.9942 2.82244 13.6917L2.11538 4.5H1.75H1V3H1.75H3.07988H5.25ZM4.31802 13.5767L3.61982 4.5H12.3802L11.682 13.5767C11.6419 14.0977 11.2075 14.5 10.6849 14.5H5.31508C4.79254 14.5 4.3581 14.0977 4.31802 13.5767Z"
      fill="currentColor"
    />
  </svg>
);

const normalizeWsBase = (rawBase) => {
  let base = String(rawBase || '').trim();
  if (!base) return '';
  if (base.startsWith('https://')) base = `wss://${base.slice(8)}`;
  if (base.startsWith('http://')) base = `ws://${base.slice(7)}`;
  if (!base.includes('/ws')) {
    base = `${base.replace(/\/$/, '')}/ws`;
  }
  return base;
};

const buildWsUrls = (sessionId, userId) => {
  if (!sessionId || !userId) return [];
  const candidates = [];
  const baseCandidates = [
    REALTIME_GATEWAY_WS_BASE,
    'wss://hk0712-theralingua-realtime-gateway.hf.space/ws'
  ];

  for (const raw of baseCandidates) {
    const normalized = normalizeWsBase(raw);
    if (!normalized) continue;
    const separator = normalized.includes('?') ? '&' : '?';
    candidates.push(`${normalized}${separator}session_id=${encodeURIComponent(sessionId)}&user_id=${encodeURIComponent(userId)}`);
  }

  return Array.from(new Set(candidates));
};

const normalizeRealtimePayload = (rawPayload) => {
  if (!rawPayload || typeof rawPayload !== 'object') return null;
  const type = String(rawPayload.type || rawPayload.event || '').trim();
  const sessionId = String(rawPayload.session_id || rawPayload.sessionId || rawPayload.session || '').trim();
  const userId = String(
    rawPayload.user_id ||
    rawPayload.userId ||
    rawPayload.sender ||
    rawPayload.sender_id ||
    '',
  ).trim();
  const content = String(rawPayload.content || rawPayload.message || '').trim();

  let serverTs = Number(rawPayload.server_ts ?? rawPayload.serverTs ?? rawPayload.timestamp ?? rawPayload.ts);
  if (!Number.isFinite(serverTs) || serverTs <= 0) {
    serverTs = Date.now();
  }

  return {
    type,
    sessionId,
    userId,
    content,
    tempId: String(rawPayload.temp_id || rawPayload.tempId || '').trim(),
    isTyping: Boolean(rawPayload.is_typing ?? rawPayload.isTyping),
    serverTs,
  };
};

const formatLastOnline = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const nowMs = Date.now();
  const diffMs = Math.max(0, nowMs - date.getTime());
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 60) {
    const minutes = Math.max(1, diffMinutes);
    return `Last online ${minutes}m ago`;
  }

  if (diffHours < 24) {
    return `Last online ${diffHours}h ago`;
  }

  if (diffDays < 7) {
    return `Last online ${diffDays}d ago`;
  }

  return `Last online ${date.toLocaleDateString()}`;
};

export default function ChatHubPanel({ isOpen, onClose, userData, presence = null }) {
  const currentUserId = userData?.id || '';

  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const { data: practiceLanguages = [] } = useQuery({
    queryKey: ['practiceLanguages'],
    queryFn: getPracticeLanguages,
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
    enabled: isOpen,
  });

  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);

  const aiSessionLanguageOptions = useMemo(
    () => [
      { value: '', label: 'General' },
      ...practiceLanguages,
    ],
    [practiceLanguages],
  );

  const [friends, setFriends] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [myTutorSessions, setMyTutorSessions] = useState([]);
  const [myStudentSessions, setMyStudentSessions] = useState([]);
  const [aiSessions, setAiSessions] = useState([]);
  const [aiModels, setAiModels] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [, setMessageCacheBySession] = useState({});
  const [unreadBySession, setUnreadBySession] = useState({});
  const [composerText, setComposerText] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [chatViewMode, setChatViewMode] = useState('docked');
  const [typingText, setTypingText] = useState('');
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isCreatingAiSession, setIsCreatingAiSession] = useState(false);
  const [isDeletingAiSession, setIsDeletingAiSession] = useState(false);
  const [isRenamingAiSession, setIsRenamingAiSession] = useState(false);
  const [isAiSending, setIsAiSending] = useState(false);
  const [lastOnlineByUser, setLastOnlineByUser] = useState({});
  const [expandedSidebarSection, setExpandedSidebarSection] = useState('dm');
  const [aiCreateNotice, setAiCreateNotice] = useState('');
  const [isAiModelMenuOpen, setIsAiModelMenuOpen] = useState(false);
  const [isAiCreatePanelOpen, setIsAiCreatePanelOpen] = useState(false);
  const [aiSessionLanguage, setAiSessionLanguage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [toast, setToast] = useState({ text: '', type: '' });
  const [editingAiSessionTitle, setEditingAiSessionTitle] = useState(false);
  const [aiSessionTitleInput, setAiSessionTitleInput] = useState('');
  const [aiComposerEditContext, setAiComposerEditContext] = useState(null);

  const composerRef = useRef(null);
  const messagesRef = useRef(null);
  const aiModelMenuRef = useRef(null);
  const dmPickerRef = useRef(null);
  const dmPickerToggleRef = useRef(null);
  const wsRef = useRef(null);
  const aiWsRef = useRef(null);
  const aiWsSessionIdRef = useRef('');
  const aiSocketPendingSessionIdRef = useRef('');
  const aiSocketReadyPromiseRef = useRef(null);
  const aiStreamStateRef = useRef({});
  const aiSuppressedUserSeqsRef = useRef({});
  const aiSuppressedSystemSeqsRef = useRef({});
  const aiStreamTimerRef = useRef(null);
  const typingSentRef = useRef(false);
  const typingTimerRef = useRef(null);
  const creatingSessionRef = useRef(false);
  const activeSessionIdRef = useRef(null);
  const activeSessionRef = useRef(null);
  const sessionsRef = useRef([]);
  const myTutorSessionsRef = useRef([]);
  const myStudentSessionsRef = useRef([]);
  const aiSessionsRef = useRef([]);
  const messageCacheRef = useRef({});
  const readMarkQueueRef = useRef({});
  const readMarkTimerRef = useRef(null);
  const pendingPanelFocusRef = useRef(null);
  const recentRealtimeInsertKeysRef = useRef(new Map());

  const combinedSessions = useMemo(() => [...sessions, ...myTutorSessions, ...myStudentSessions, ...aiSessions], [sessions, myTutorSessions, myStudentSessions, aiSessions]);
  const activeSession = useMemo(() => combinedSessions.find((session) => session.id === activeSessionId) || null, [combinedSessions, activeSessionId]);
  const activePeerId = activeSession?.chatKind === 'dm' || activeSession?.chatKind === 'tutor_student' ? activeSession?.targetUserId || '' : '';
  const isActiveAiSession = activeSession?.chatKind === 'ai';

  const sortedSessions = useMemo(() => [...sessions].sort((a, b) => b.updatedAt - a.updatedAt), [sessions]);
  const sortedMyTutorSessions = useMemo(() => [...myTutorSessions].sort((a, b) => b.updatedAt - a.updatedAt), [myTutorSessions]);
  const sortedMyStudentSessions = useMemo(() => [...myStudentSessions].sort((a, b) => b.updatedAt - a.updatedAt), [myStudentSessions]);
  const sortedAiSessions = useMemo(() => [...aiSessions].sort((a, b) => b.updatedAt - a.updatedAt), [aiSessions]);
  const availableDmFriends = useMemo(() => {
    const existingTargetIds = new Set(
      sessions
        .map((session) => String(session.targetUserId || '').trim())
        .filter(Boolean),
    );

    return friends.filter((friend) => !existingTargetIds.has(String(friend.id || '').trim()));
  }, [friends, sessions]);
  const hasDmUnread = useMemo(
    () => sortedSessions.some((session) => (unreadBySession[session.id] || 0) > 0),
    [sortedSessions, unreadBySession],
  );
  const hasMyTutorUnread = useMemo(
    () => sortedMyTutorSessions.some((session) => (unreadBySession[session.id] || 0) > 0),
    [sortedMyTutorSessions, unreadBySession],
  );
  const hasMyStudentUnread = useMemo(
    () => sortedMyStudentSessions.some((session) => (unreadBySession[session.id] || 0) > 0),
    [sortedMyStudentSessions, unreadBySession],
  );
  const selectedAiModel = useMemo(() => {
    if (!isActiveAiSession) return null;
    const currentModelId = String(activeSession?.currentModelId || '').trim();
    if (!currentModelId) return null;
    return aiModels.find((model) => model.modelId === currentModelId) || null;
  }, [isActiveAiSession, activeSession, aiModels]);
  const activeAiBlockingWarning = useMemo(() => {
    if (!isActiveAiSession) return null;

    const normalizedSessionId = String(activeSession?.id || '').trim();
    if (!normalizedSessionId) return null;

    const sessionMessages = messageCacheRef.current[normalizedSessionId] || messages;
    const latestBlockingError = [...sessionMessages]
      .reverse()
      .find((msg) => {
        if (msg.kind !== 'system_error') return false;
        const normalizedText = String(msg.text || '').toLowerCase();
        return normalizedText.includes('api key')
          || normalizedText.includes('unauthorized')
          || normalizedText.includes('invalid');
      });

    if (!latestBlockingError) return null;

    return {
      text: String(latestBlockingError.text || 'Update your API key to continue chatting.'),
      type: 'credentials',
    };
  }, [isActiveAiSession, activeSession, messages]);

  const threadMeta = useMemo(() => {
    if (activeSession?.chatKind === 'ai') {
      return {
        text: activeSession.currentModelName || 'No model selected',
        status: 'offline',
      };
    }
    if (typingText) return { text: typingText, status: 'typing' };
    if (!activePeerId) return { text: '', status: 'offline' };

    if (presence?.isUserOnline?.(activePeerId)) {
      return { text: 'Online', status: 'online' };
    }

    const lastOnline = presence?.getLastOnline?.(activePeerId) || lastOnlineByUser[activePeerId];
    const text = formatLastOnline(lastOnline);
    return { text, status: 'offline' };
  }, [activeSession, typingText, activePeerId, presence, lastOnlineByUser]);

  const resolveAvatarUrl = useCallback((avatarPath) => {
    if (!avatarPath) return DEFAULT_AVATAR_URL;
    if (isExternalUrl(avatarPath)) return avatarPath;
    if (!publicBaseUrl) return avatarPath;
    return `${publicBaseUrl}/${avatarPath}`;
  }, [publicBaseUrl]);

  const scrollMessagesToBottom = useCallback((behavior = 'auto') => {
    const container = messagesRef.current;
    if (!container) return;
    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
  }, []);

  const shouldProcessRealtimeInsert = useCallback((tableName, row) => {
    const normalizedTableName = String(tableName || '').trim();
    const sessionId = String(row?.session_id || '').trim();
    if (!normalizedTableName || !sessionId) return true;

    const messageSeq = Number(row?.message_seq ?? 0);
    const uniquePart = Number.isFinite(messageSeq) && messageSeq > 0
      ? `seq:${messageSeq}`
      : `fallback:${String(row?.sender || '').trim()}:${String(row?.sent_at || '').trim()}:${String(row?.message || '').trim()}`;

    const cacheKey = `${normalizedTableName}:${sessionId}:${uniquePart}`;
    const now = Date.now();
    const cache = recentRealtimeInsertKeysRef.current;

    for (const [key, seenAt] of cache.entries()) {
      if (now - seenAt > REALTIME_INSERT_DEDUPE_KEEP_MS) {
        cache.delete(key);
      }
    }

    if (cache.has(cacheKey)) {
      return false;
    }

    cache.set(cacheKey, now);
    return true;
  }, []);

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  useEffect(() => {
    activeSessionRef.current = activeSession;
  }, [activeSession]);

  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  useEffect(() => {
    myTutorSessionsRef.current = myTutorSessions;
  }, [myTutorSessions]);

  useEffect(() => {
    myStudentSessionsRef.current = myStudentSessions;
  }, [myStudentSessions]);

  useEffect(() => {
    aiSessionsRef.current = aiSessions;
  }, [aiSessions]);

  const updateSessionCache = useCallback((sessionId, computeNextMessages) => {
    if (!sessionId || typeof computeNextMessages !== 'function') return [];
    const current = messageCacheRef.current[sessionId] || [];
    const nextMessages = computeNextMessages(current);
    if (!Array.isArray(nextMessages)) return current;
    const nextCache = {
      ...messageCacheRef.current,
      [sessionId]: nextMessages,
    };
    messageCacheRef.current = nextCache;
    setMessageCacheBySession(nextCache);
    return nextMessages;
  }, []);

  const applySessionMessages = useCallback((sessionId, computeNextMessages) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId || typeof computeNextMessages !== 'function') return [];

    const nextMessages = updateSessionCache(normalizedSessionId, computeNextMessages);
    if (activeSessionIdRef.current === normalizedSessionId) {
      setMessages(nextMessages);
    }
    return nextMessages;
  }, [updateSessionCache]);

  const removeAiMessages = useCallback((sessionId, shouldRemove) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId || typeof shouldRemove !== 'function') return [];

    return applySessionMessages(normalizedSessionId, (current) =>
      current.filter((msg) => !shouldRemove(msg)),
    );
  }, [applySessionMessages]);

  const clearAiPendingAssistant = useCallback((sessionId, clientTempId) => {
    const normalizedClientTempId = String(clientTempId || '').trim();
    if (!sessionId || !normalizedClientTempId) return;

    delete aiStreamStateRef.current[normalizedClientTempId];
    removeAiMessages(sessionId, (msg) =>
      msg.sender === 'assistant'
      && msg.clientTempId === normalizedClientTempId
      && msg.pending,
    );
  }, [removeAiMessages]);

  const upsertAiSystemErrorMessage = useCallback((sessionId, payload = {}) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId) return null;

    const relatedUserClientTempId = String(payload.client_temp_id || payload.relatedUserClientTempId || '').trim();
    const rawUserMessageSeq = payload.user_message_seq ?? payload.relatedUserMessageSeq;
    const relatedUserMessageSeq = Number.isFinite(rawUserMessageSeq) && Number(rawUserMessageSeq) > 0
      ? Number(rawUserMessageSeq)
      : null;
    const relationKey = relatedUserMessageSeq != null
      ? `seq-${relatedUserMessageSeq}`
      : relatedUserClientTempId
        ? `temp-${relatedUserClientTempId}`
        : `ephemeral-${Date.now()}`;
    const persistedMessageSeq = Number.isFinite(payload.message_seq) && Number(payload.message_seq) > 0
      ? Number(payload.message_seq)
      : null;
    const errorId = String(payload.id || persistedMessageSeq || `ai-error-${normalizedSessionId}-${relationKey}`).trim();
    const userVisibleText = String(
      payload.user_visible_message
      || payload.userVisibleMessage
      || payload.message
      || payload.error
      || payload.text
      || 'Something went wrong while generating the AI reply.',
    ).trim();
    const errorAt = payload.sent_at ? new Date(payload.sent_at).getTime() : Date.now();

    const errorMessage = {
      id: errorId,
      legacyId: null,
      messageSeq: persistedMessageSeq,
      sessionId: normalizedSessionId,
      sender: 'system',
      role: 'system',
      kind: 'system_error',
      text: userVisibleText,
      at: errorAt,
      pending: false,
      failed: true,
      promptEligible: false,
      errorCode: String(payload.code || '').trim(),
      errorPhase: String(payload.phase || 'generation').trim(),
      retryable: Boolean(payload.retryable),
      relatedUserClientTempId,
      relatedUserMessageSeq,
    };

    applySessionMessages(normalizedSessionId, (current) => {
      const nextMessages = current.filter((msg) => {
        if (msg.kind !== 'system_error') return true;
        if (msg.id === errorId) return false;
        if (relatedUserMessageSeq != null && msg.relatedUserMessageSeq === relatedUserMessageSeq) return false;
        if (relatedUserClientTempId && msg.relatedUserClientTempId === relatedUserClientTempId) return false;
        return true;
      });
      return [...nextMessages, errorMessage].sort((a, b) => a.at - b.at);
    });

    return errorMessage;
  }, [applySessionMessages]);

  const findAiRelatedUserMessage = useCallback((sessionId, errorMessage) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId || !errorMessage) return null;

    const sessionMessages = messageCacheRef.current[normalizedSessionId] || [];
    const targetUserMessageSeq = Number.isFinite(errorMessage.relatedUserMessageSeq)
      ? Number(errorMessage.relatedUserMessageSeq)
      : null;
    const targetClientTempId = String(errorMessage.relatedUserClientTempId || '').trim();

    const directMatch = sessionMessages.find((msg) => {
      if (msg.kind === 'system_error') return false;
      if (msg.role !== 'me') return false;
      if (targetUserMessageSeq != null && msg.messageSeq === targetUserMessageSeq) return true;
      if (targetClientTempId && (msg.id === targetClientTempId || msg.clientTempId === targetClientTempId)) return true;
      return false;
    }) || null;

    if (directMatch) return directMatch;

    const currentErrorIndex = sessionMessages.findIndex((msg) => msg.id === errorMessage.id);
    if (currentErrorIndex <= 0) return null;

    for (let index = currentErrorIndex - 1; index >= 0; index -= 1) {
      const candidate = sessionMessages[index];
      if (candidate.kind === 'system_error') continue;
      if (candidate.role === 'me') return candidate;
      if (candidate.role === 'other') break;
    }

    return null;
  }, []);

  const focusComposer = useCallback(() => {
    requestAnimationFrame(() => {
      composerRef.current?.focus();
    });
  }, []);

  const suppressAiUserMessageSeq = useCallback((sessionId, messageSeq) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId || !Number.isFinite(messageSeq) || Number(messageSeq) <= 0) return;
    const currentSet = aiSuppressedUserSeqsRef.current[normalizedSessionId] || new Set();
    currentSet.add(Number(messageSeq));
    aiSuppressedUserSeqsRef.current = {
      ...aiSuppressedUserSeqsRef.current,
      [normalizedSessionId]: currentSet,
    };
  }, []);

  const suppressAiSystemMessageSeq = useCallback((sessionId, messageSeq) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId || !Number.isFinite(messageSeq) || Number(messageSeq) <= 0) return;
    const currentSet = aiSuppressedSystemSeqsRef.current[normalizedSessionId] || new Set();
    currentSet.add(Number(messageSeq));
    aiSuppressedSystemSeqsRef.current = {
      ...aiSuppressedSystemSeqsRef.current,
      [normalizedSessionId]: currentSet,
    };
  }, []);

  const removeAiSystemErrorMessages = useCallback((sessionId, filters = {}) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId) return;

    const targetErrorId = String(filters.errorId || '').trim();
    const targetClientTempId = String(filters.relatedUserClientTempId || '').trim();
    const targetErrorText = String(filters.errorText || '').trim();
    const targetUserMessageSeq = Number.isFinite(filters.relatedUserMessageSeq)
      ? Number(filters.relatedUserMessageSeq)
      : null;

    const currentMessages = messageCacheRef.current[normalizedSessionId] || [];
    currentMessages.forEach((msg) => {
      if (msg.kind !== 'system_error') return;
      const matches =
        (targetErrorId && msg.id === targetErrorId)
        || (targetUserMessageSeq != null && msg.relatedUserMessageSeq === targetUserMessageSeq)
        || (targetClientTempId && msg.relatedUserClientTempId === targetClientTempId)
        || (targetErrorText && String(msg.text || '').trim() === targetErrorText);
      if (matches && Number.isFinite(msg.messageSeq) && Number(msg.messageSeq) > 0) {
        suppressAiSystemMessageSeq(normalizedSessionId, Number(msg.messageSeq));
      }
    });

    removeAiMessages(normalizedSessionId, (msg) => {
      if (msg.kind !== 'system_error') return false;
      if (targetErrorId && msg.id === targetErrorId) return true;
      if (targetUserMessageSeq != null && msg.relatedUserMessageSeq === targetUserMessageSeq) return true;
      if (targetClientTempId && msg.relatedUserClientTempId === targetClientTempId) return true;
      if (targetErrorText && String(msg.text || '').trim() === targetErrorText) return true;
      return false;
    });
  }, [removeAiMessages, suppressAiSystemMessageSeq]);

  const clearAiFailedTurn = useCallback(async (errorMessage, relatedUserMessage) => {
    if (!errorMessage?.sessionId || !relatedUserMessage) return;

    const relatedUserMessageSeq = Number.isFinite(relatedUserMessage.messageSeq) && Number(relatedUserMessage.messageSeq) > 0
      ? Number(relatedUserMessage.messageSeq)
      : null;
    const relatedSystemMessageSeq = Number.isFinite(errorMessage.messageSeq) && Number(errorMessage.messageSeq) > 0
      ? Number(errorMessage.messageSeq)
      : null;

    if (relatedUserMessageSeq != null) {
      suppressAiUserMessageSeq(errorMessage.sessionId, relatedUserMessageSeq);
    }

    clearAiPendingAssistant(
      errorMessage.sessionId,
      String(errorMessage.relatedUserClientTempId || relatedUserMessage.clientTempId || relatedUserMessage.id || '').trim(),
    );

    removeAiSystemErrorMessages(errorMessage.sessionId, {
      errorId: errorMessage.id,
      errorText: errorMessage.text,
      relatedUserClientTempId: String(
        errorMessage.relatedUserClientTempId || relatedUserMessage.clientTempId || relatedUserMessage.id || '',
      ).trim(),
      relatedUserMessageSeq,
    });

    removeAiMessages(errorMessage.sessionId, (msg) => {
      if (msg.role !== 'me') return false;
      if (Number.isFinite(relatedUserMessage.messageSeq) && msg.messageSeq === Number(relatedUserMessage.messageSeq)) {
        return true;
      }
      const targetClientTempId = String(
        errorMessage.relatedUserClientTempId || relatedUserMessage.clientTempId || relatedUserMessage.id || '',
      ).trim();
      if (targetClientTempId && (msg.id === targetClientTempId || msg.clientTempId === targetClientTempId)) {
        return true;
      }
      return false;
    });

    if (relatedUserMessageSeq != null || relatedSystemMessageSeq != null) {
      try {
        await deleteAiChatTurnMessages(
          errorMessage.sessionId,
          relatedUserMessageSeq,
          relatedSystemMessageSeq,
        );
      } catch (error) {
        console.warn('Failed to clear AI failed turn in DB:', error);
      }
    }
  }, [
    clearAiPendingAssistant,
    deleteAiChatTurnMessages,
    removeAiMessages,
    removeAiSystemErrorMessages,
    suppressAiUserMessageSeq,
  ]);

  const flushReadMarks = useCallback(async () => {
    const queue = readMarkQueueRef.current;
    const entries = Object.entries(queue);
    if (!currentUserId || entries.length === 0) return;

    for (const [sessionId, payload] of entries) {
      const readAt = typeof payload === 'string' ? payload : payload?.readAt;
      const mustBeActive = Boolean(payload && typeof payload === 'object' && payload.mustBeActive);
      if (!readAt) {
        delete readMarkQueueRef.current[sessionId];
        continue;
      }
      if (mustBeActive && activeSessionIdRef.current !== sessionId) {
        delete readMarkQueueRef.current[sessionId];
        continue;
      }
      try {
        await markChatSessionRead(sessionId, currentUserId, readAt);
        delete readMarkQueueRef.current[sessionId];
      } catch (error) {
        console.warn('Failed to update last_read_at:', error);
      }
    }
  }, [currentUserId]);

  const scheduleReadMark = useCallback((sessionId, immediate = false, readAtOverride = null) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId || !currentUserId) return;

    readMarkQueueRef.current[normalizedSessionId] = {
      readAt: readAtOverride || new Date().toISOString(),
      mustBeActive: true,
    };

    if (immediate) {
      if (readMarkTimerRef.current) {
        clearTimeout(readMarkTimerRef.current);
        readMarkTimerRef.current = null;
      }
      void flushReadMarks();
      return;
    }

    if (readMarkTimerRef.current) return;
    readMarkTimerRef.current = setTimeout(() => {
      readMarkTimerRef.current = null;
      void flushReadMarks();
    }, READ_MARK_FLUSH_DELAY_MS);
  }, [currentUserId, flushReadMarks]);

  const loadFriends = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const rows = await getFriendships(currentUserId);
      setFriends(rows || []);
    } catch (error) {
      console.error('Failed to load friends:', error);
      setFriends([]);
    }
  }, [currentUserId]);

  const loadSessions = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const rows = await getDirectMessageSessions(currentUserId);
      setSessions(rows || []);
      const sessionIds = (rows || []).map((session) => session.id);
      const unreadMap = await getUnreadCountsBySessions(currentUserId, sessionIds);
      setUnreadBySession((prev) => {
        const next = { ...prev };
        sessionIds.forEach((sessionId) => {
          next[sessionId] = Number(unreadMap?.[sessionId] || 0);
        });
        return next;
      });
      const peerIds = Array.from(
        new Set(
          (rows || [])
            .map((session) => String(session.targetUserId || '').trim())
            .filter(Boolean),
        ),
      );
      if (peerIds.length > 0) {
        const lastOnlineMap = await getProfilesLastOnline(peerIds);
        setLastOnlineByUser((prev) => ({ ...prev, ...(lastOnlineMap || {}) }));
      }
      setActiveSessionId((prev) => {
        if (prev && (rows || []).some((session) => session.id === prev)) {
          return prev;
        }
        if (prev && myTutorSessionsRef.current.some((session) => session.id === prev)) {
          return prev;
        }
        if (prev && myStudentSessionsRef.current.some((session) => session.id === prev)) {
          return prev;
        }
        if (prev && aiSessionsRef.current.some((session) => session.id === prev)) {
          return prev;
        }
        if (prev) {
          delete readMarkQueueRef.current[String(prev).trim()];
          activeSessionIdRef.current = null;
        }
        return null;
      });
    } catch (error) {
      console.error('Failed to load sessions:', error);
      setSessions([]);
      if (activeSessionIdRef.current) {
        const activeStillExists =
          aiSessionsRef.current.some((session) => session.id === activeSessionIdRef.current)
          || myTutorSessionsRef.current.some((session) => session.id === activeSessionIdRef.current)
          || myStudentSessionsRef.current.some((session) => session.id === activeSessionIdRef.current);
        if (!activeStillExists) {
          delete readMarkQueueRef.current[String(activeSessionIdRef.current).trim()];
          activeSessionIdRef.current = null;
        }
      }
      if (
        !aiSessionsRef.current.some((session) => session.id === activeSessionIdRef.current)
        && !myTutorSessionsRef.current.some((session) => session.id === activeSessionIdRef.current)
        && !myStudentSessionsRef.current.some((session) => session.id === activeSessionIdRef.current)
      ) {
        setActiveSessionId(null);
      }
    }
  }, [currentUserId]);

  const loadTutorStudentSessions = useCallback(async (options = {}) => {
    if (!currentUserId) return;
    const shouldRefreshUnread = options.refreshUnread !== false;
    try {
      const payload = await getTutorStudentChatSessions(currentUserId);
      const nextMyTutors = payload?.myTutors || [];
      const nextMyStudents = payload?.myStudents || [];
      setMyTutorSessions(nextMyTutors);
      setMyStudentSessions(nextMyStudents);

      const sessionIds = [...nextMyTutors, ...nextMyStudents].map((session) => session.id);
      if (shouldRefreshUnread) {
        const unreadMap = await getTutorStudentUnreadCountsBySessions(currentUserId, sessionIds);
        setUnreadBySession((prev) => ({ ...prev, ...(unreadMap || {}) }));
      }

      const peerIds = Array.from(new Set(
        [...nextMyTutors, ...nextMyStudents]
          .map((session) => String(session.targetUserId || '').trim())
          .filter(Boolean),
      ));
      if (peerIds.length > 0) {
        const lastOnlineMap = await getProfilesLastOnline(peerIds);
        setLastOnlineByUser((prev) => ({ ...prev, ...(lastOnlineMap || {}) }));
      }
      setActiveSessionId((prev) => {
        if (prev && sessionIds.includes(prev)) {
          return prev;
        }
        if (prev && sessionsRef.current.some((session) => session.id === prev)) {
          return prev;
        }
        if (prev && aiSessionsRef.current.some((session) => session.id === prev)) {
          return prev;
        }
        if (prev) {
          delete readMarkQueueRef.current[String(prev).trim()];
          activeSessionIdRef.current = null;
        }
        return null;
      });
    } catch (error) {
      console.error('Failed to load tutor-student sessions:', error);
      setMyTutorSessions([]);
      setMyStudentSessions([]);
      if (
        activeSessionIdRef.current
        && !sessionsRef.current.some((session) => session.id === activeSessionIdRef.current)
        && !aiSessionsRef.current.some((session) => session.id === activeSessionIdRef.current)
      ) {
        delete readMarkQueueRef.current[String(activeSessionIdRef.current).trim()];
        activeSessionIdRef.current = null;
        setActiveSessionId(null);
      }
    }
  }, [currentUserId]);

  const updateRegularSessionLists = useCallback((updater) => {
    if (typeof updater !== 'function') return;
    setSessions((prev) => updater(prev));
    setMyTutorSessions((prev) => updater(prev));
    setMyStudentSessions((prev) => updater(prev));
  }, []);

  const loadAiSessions = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const rows = await getAiChatSessions(currentUserId);
      setAiSessions(rows || []);
      setActiveSessionId((prev) => {
        if (prev && (rows || []).some((session) => session.id === prev)) {
          return prev;
        }
        if (prev && sessionsRef.current.some((session) => session.id === prev)) {
          return prev;
        }
        if (prev && myTutorSessionsRef.current.some((session) => session.id === prev)) {
          return prev;
        }
        if (prev && myStudentSessionsRef.current.some((session) => session.id === prev)) {
          return prev;
        }
        if (prev) {
          delete readMarkQueueRef.current[String(prev).trim()];
        }
        return null;
      });
      setAiCreateNotice('');
    } catch (error) {
      console.error('Failed to load ai chat sessions:', error);
      setAiSessions([]);
      if (
        !sessionsRef.current.some((session) => session.id === activeSessionIdRef.current)
        && !myTutorSessionsRef.current.some((session) => session.id === activeSessionIdRef.current)
        && !myStudentSessionsRef.current.some((session) => session.id === activeSessionIdRef.current)
      ) {
        setActiveSessionId(null);
      }
    }
  }, [currentUserId]);

  const closeAiSocket = useCallback(() => {
    aiSocketReadyPromiseRef.current = null;
    aiSocketPendingSessionIdRef.current = '';
    aiWsSessionIdRef.current = '';
    if (aiWsRef.current) {
      aiWsRef.current.close();
      aiWsRef.current = null;
    }
  }, []);

  const clearAiStreamPlayback = useCallback(() => {
    aiStreamStateRef.current = {};
    if (aiStreamTimerRef.current) {
      clearTimeout(aiStreamTimerRef.current);
      aiStreamTimerRef.current = null;
    }
  }, []);

  const finalizeAiStreamMessage = useCallback((sessionId, clientTempId, payload) => {
    if (!sessionId || !clientTempId || !payload) return;
    const savedAt = payload.sent_at ? new Date(payload.sent_at).getTime() : Date.now();
    const finalMessageText = payload.message || '';
    const finalMessageSeq = payload.message_seq ?? null;

    applySessionMessages(sessionId, (current) => {
      let baseAssistantMessage = null;
      const nextMessages = [];

      current.forEach((msg) => {
        const isAssistantMessage = msg.sender === 'assistant';
        const matchesClientTempId = isAssistantMessage && msg.clientTempId === clientTempId;
        const matchesFinalMessageSeq = isAssistantMessage && finalMessageSeq != null && msg.id === finalMessageSeq;

        if ((matchesClientTempId || matchesFinalMessageSeq) && !baseAssistantMessage) {
          baseAssistantMessage = msg;
          return;
        }

        if (matchesClientTempId || matchesFinalMessageSeq) {
          return;
        }

        nextMessages.push(msg);
      });

      nextMessages.push({
        ...(baseAssistantMessage || {
          id: finalMessageSeq || `assistant-${clientTempId}`,
          legacyId: null,
          messageSeq: null,
          sessionId,
          sender: 'assistant',
          text: '',
          at: savedAt,
          role: 'other',
          pending: false,
          failed: false,
          modelId: '',
          clientTempId: '',
        }),
        id: finalMessageSeq || baseAssistantMessage?.id || `assistant-${clientTempId}`,
        messageSeq: finalMessageSeq ?? baseAssistantMessage?.messageSeq ?? null,
        sessionId,
        sender: 'assistant',
        text: finalMessageText || baseAssistantMessage?.text || '',
        at: savedAt,
        role: 'other',
        pending: false,
        failed: false,
        modelId: payload.model_id || baseAssistantMessage?.modelId || '',
        clientTempId: '',
      });

      return nextMessages.sort((a, b) => a.at - b.at);
    });

    setAiSessions((prev) =>
      prev.map((session) =>
        session.id === sessionId
          ? {
              ...session,
              preview: finalMessageText || session.preview,
              updatedAt: savedAt,
              currentModelId: payload.model_id || session.currentModelId,
              currentModelName:
                session.id === activeSessionIdRef.current
                  ? activeSessionRef.current?.currentModelName || session.currentModelName
                  : session.currentModelName,
            }
          : session,
      ),
    );

    setIsAiSending(false);
  }, [applySessionMessages]);

  const flushAiStreamPlayback = useCallback(() => {
    aiStreamTimerRef.current = null;

    const streamEntries = Object.entries(aiStreamStateRef.current);
    if (streamEntries.length === 0) return;

    let shouldContinue = false;

    for (const [clientTempId, state] of streamEntries) {
      if (!state || !state.sessionId) continue;

      const backlogSize = state.queue.length;
      const effectiveCharsPerSecond = Math.max(
        AI_STREAM_MIN_CHARS_PER_SECOND,
        Math.min(
          AI_STREAM_MAX_CHARS_PER_SECOND,
          backlogSize >= AI_STREAM_BACKLOG_BOOST_THRESHOLD
            ? state.targetCharsPerSecond * 1.35
            : state.targetCharsPerSecond,
        ),
      );
      const charsPerTick = Math.max(
        1,
        Math.ceil((effectiveCharsPerSecond * AI_STREAM_RENDER_TICK_MS) / 1000),
      );
      const nextDelta = state.queue.slice(0, charsPerTick);

      if (nextDelta) {
        state.queue = state.queue.slice(nextDelta.length);
        state.renderedText = `${state.renderedText}${nextDelta}`;

        setMessages((prev) =>
          prev.map((msg) =>
            msg.clientTempId === clientTempId && msg.sender === 'assistant'
              ? { ...msg, text: state.renderedText }
              : msg,
          ),
        );
        updateSessionCache(state.sessionId, (current) =>
          current.map((msg) =>
            msg.clientTempId === clientTempId && msg.sender === 'assistant'
              ? { ...msg, text: state.renderedText }
              : msg,
          ),
        );
      }

      if (!state.queue.length && state.donePayload) {
        finalizeAiStreamMessage(state.sessionId, clientTempId, state.donePayload);
        delete aiStreamStateRef.current[clientTempId];
        continue;
      }

      if (state.queue.length > 0 || state.donePayload) {
        shouldContinue = true;
      }
    }

    if (shouldContinue) {
      aiStreamTimerRef.current = setTimeout(flushAiStreamPlayback, AI_STREAM_RENDER_TICK_MS);
    }
  }, [finalizeAiStreamMessage, updateSessionCache]);

  const scheduleAiStreamPlayback = useCallback(() => {
    if (aiStreamTimerRef.current) return;
    aiStreamTimerRef.current = setTimeout(flushAiStreamPlayback, AI_STREAM_RENDER_TICK_MS);
  }, [flushAiStreamPlayback]);

  const handleAiSocketMessage = useCallback((event) => {
    try {
      const payload = JSON.parse(event.data || '{}');
      const sessionId = String(payload?.session_id || '').trim();
      if (!payload || !sessionId) return;

      const activeAiSession = activeSessionRef.current;
      const activeAiSessionId = activeAiSession?.chatKind === 'ai'
        ? String(activeAiSession.id || '').trim()
        : '';

      if (sessionId !== aiWsSessionIdRef.current && sessionId !== activeAiSessionId) {
        return;
      }

      if (payload.type === 'connected') {
        return;
      }

      if (payload.type === 'message_saved' && payload.role === 'user') {
        const savedAt = payload.sent_at ? new Date(payload.sent_at).getTime() : Date.now();
        applySessionMessages(sessionId, (current) =>
          current.map((msg) =>
            msg.id === payload.client_temp_id
              ? {
                  ...msg,
                  id: payload.message_seq || msg.id,
                  messageSeq: payload.message_seq ?? msg.messageSeq ?? null,
                  at: savedAt,
                  pending: false,
                  failed: false,
                  clientTempId: payload.client_temp_id || msg.clientTempId || '',
                }
              : msg,
          ),
        );
        removeAiSystemErrorMessages(sessionId, {
          relatedUserClientTempId: payload.client_temp_id,
          relatedUserMessageSeq: payload.message_seq,
        });
        return;
      }

      if (payload.type === 'session_named' && payload.session_name) {
        setAiSessions((prev) =>
          prev.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  name: payload.session_name,
                }
              : session,
          ),
        );
        return;
      }

      if (payload.type === 'assistant_started') {
        const assistantTempId = `assistant-${payload.client_temp_id || Date.now()}`;
        aiStreamStateRef.current[payload.client_temp_id || assistantTempId] = {
          sessionId,
          queue: '',
          renderedText: '',
          targetCharsPerSecond: 34,
          lastChunkAt: 0,
          donePayload: null,
        };

        const optimisticAssistant = {
          id: assistantTempId,
          legacyId: null,
          messageSeq: null,
          sessionId,
          sender: 'assistant',
          text: '',
          at: Date.now(),
          role: 'other',
          pending: true,
          failed: false,
          modelId: payload.model_id || activeAiSession?.currentModelId || '',
          clientTempId: payload.client_temp_id || '',
        };

        applySessionMessages(sessionId, (current) => {
          if (current.some((msg) => msg.clientTempId === optimisticAssistant.clientTempId && msg.sender === 'assistant')) {
            return current;
          }
          return [...current, optimisticAssistant];
        });
        return;
      }

      if (payload.type === 'assistant_resume') {
        const clientTempId = payload.client_temp_id || '';
        if (!clientTempId) {
          return;
        }

        const resumedText = String(payload.message || '');
        aiStreamStateRef.current[clientTempId] = {
          sessionId,
          queue: '',
          renderedText: resumedText,
          targetCharsPerSecond: 34,
          lastChunkAt: 0,
          donePayload: null,
        };

        const resumedAssistant = {
          id: `assistant-${clientTempId}`,
          legacyId: null,
          messageSeq: null,
          sessionId,
          sender: 'assistant',
          text: resumedText,
          at: Date.now(),
          role: 'other',
          pending: true,
          failed: false,
          modelId: payload.model_id || activeAiSession?.currentModelId || '',
          clientTempId,
        };

        applySessionMessages(sessionId, (current) => {
          const existingIndex = current.findIndex(
            (msg) => msg.clientTempId === clientTempId && msg.sender === 'assistant',
          );

          if (existingIndex >= 0) {
            const next = [...current];
            next[existingIndex] = {
              ...next[existingIndex],
              text: resumedText || next[existingIndex].text,
              pending: true,
              failed: false,
              modelId: resumedAssistant.modelId || next[existingIndex].modelId || '',
            };
            return next;
          }

          return [...current, resumedAssistant];
        });
        return;
      }

      if (payload.type === 'assistant_chunk' && payload.delta) {
        const clientTempId = payload.client_temp_id || '';
        const now = performance.now();
        const existingState = aiStreamStateRef.current[clientTempId] || {
          sessionId,
          queue: '',
          renderedText: '',
          targetCharsPerSecond: 34,
          lastChunkAt: 0,
          donePayload: null,
        };
        const deltaChars = String(payload.delta).length;
        if (existingState.lastChunkAt > 0 && deltaChars > 0) {
          const elapsedMs = Math.max(16, now - existingState.lastChunkAt);
          const measuredCharsPerSecond = (deltaChars * 1000) / elapsedMs;
          const clampedCharsPerSecond = Math.max(
            AI_STREAM_MIN_CHARS_PER_SECOND,
            Math.min(AI_STREAM_MAX_CHARS_PER_SECOND, measuredCharsPerSecond),
          );
          existingState.targetCharsPerSecond = existingState.targetCharsPerSecond > 0
            ? (existingState.targetCharsPerSecond * 0.65) + (clampedCharsPerSecond * 0.35)
            : clampedCharsPerSecond;
        }
        existingState.lastChunkAt = now;
        existingState.queue = `${existingState.queue}${payload.delta}`;
        aiStreamStateRef.current[clientTempId] = existingState;
        scheduleAiStreamPlayback();
        return;
      }

      if (payload.type === 'assistant_done') {
        const clientTempId = payload.client_temp_id || '';
        const existingState = aiStreamStateRef.current[clientTempId];
        if (existingState) {
          existingState.donePayload = payload;
          aiStreamStateRef.current[clientTempId] = existingState;
          scheduleAiStreamPlayback();
        } else {
          finalizeAiStreamMessage(sessionId, clientTempId, payload);
        }
        removeAiSystemErrorMessages(sessionId, {
          relatedUserClientTempId: clientTempId,
        });
        return;
      }

      if (payload.type === 'error') {
        setIsAiSending(false);
        clearAiPendingAssistant(sessionId, payload.client_temp_id || '');
        if (!payload.user_message_seq && payload.client_temp_id) {
          applySessionMessages(sessionId, (current) =>
            current.map((msg) =>
              msg.id === payload.client_temp_id
                ? {
                    ...msg,
                    pending: false,
                    failed: true,
                  }
                : msg,
            ),
          );
        }
        upsertAiSystemErrorMessage(sessionId, payload);
        console.error('AI chat websocket error:', payload.error || payload.code || 'Unknown AI websocket error');
      }
    } catch (error) {
      console.error('Failed to parse AI websocket payload:', error);
    }
  }, [
    applySessionMessages,
    clearAiPendingAssistant,
    finalizeAiStreamMessage,
    removeAiSystemErrorMessages,
    scheduleAiStreamPlayback,
    upsertAiSystemErrorMessage,
  ]);

  const ensureAiSocketReady = useCallback(async (sessionId) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId) throw new Error('sessionId is required.');

    if (
      aiWsRef.current
      && aiWsSessionIdRef.current === normalizedSessionId
      && aiWsRef.current.readyState === WebSocket.OPEN
    ) {
      return aiWsRef.current;
    }

    if (
      aiSocketReadyPromiseRef.current
      && aiSocketPendingSessionIdRef.current === normalizedSessionId
    ) {
      return aiSocketReadyPromiseRef.current;
    }

    aiSocketPendingSessionIdRef.current = normalizedSessionId;
    aiSocketReadyPromiseRef.current = (async () => {
      const { token, ws_url: wsUrl } = await startAiChatSession(normalizedSessionId);
      if (!token || !wsUrl) {
        throw new Error('AI chat gateway connection data is missing.');
      }

      closeAiSocket();

      const finalWsUrl = `${String(wsUrl).trim()}?token=${encodeURIComponent(String(token).trim())}`;
      aiWsSessionIdRef.current = normalizedSessionId;

      const socket = await new Promise((resolve, reject) => {
        const nextSocket = new WebSocket(finalWsUrl);
        let settled = false;

        nextSocket.addEventListener('message', handleAiSocketMessage);

        const timeoutId = setTimeout(() => {
          if (settled) return;
          settled = true;
          nextSocket.close();
          reject(new Error('Timed out while connecting to AI chat gateway.'));
        }, AI_SOCKET_READY_TIMEOUT_MS);

        nextSocket.onopen = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          resolve(nextSocket);
        };

        nextSocket.onerror = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          reject(new Error('Failed to connect to AI chat gateway.'));
        };

        nextSocket.onclose = () => {
          if (!settled) {
            settled = true;
            clearTimeout(timeoutId);
            reject(new Error('AI chat gateway connection closed before ready.'));
          }
        };
      });

      socket.addEventListener('close', () => {
        if (aiWsRef.current === socket) {
          aiWsRef.current = null;
          aiSocketPendingSessionIdRef.current = '';
          aiWsSessionIdRef.current = '';
          aiSocketReadyPromiseRef.current = null;
        }
      });

      aiWsRef.current = socket;
      aiSocketPendingSessionIdRef.current = normalizedSessionId;
      aiSocketReadyPromiseRef.current = Promise.resolve(socket);
      return socket;
    })();

    try {
      return await aiSocketReadyPromiseRef.current;
    } catch (error) {
      aiSocketReadyPromiseRef.current = null;
      aiSocketPendingSessionIdRef.current = '';
      aiWsSessionIdRef.current = '';
      aiWsRef.current = null;
      throw error;
    }
  }, [closeAiSocket, handleAiSocketMessage]);

  const loadAvailableAiModels = useCallback(async () => {
    try {
      const rows = await getAvailableAiModels();
      setAiModels(rows || []);
    } catch (error) {
      console.error('Failed to load ai models:', error);
      setAiModels([]);
    }
  }, []);

  const refreshUnreadForSessions = useCallback(async (sessionIds = null) => {
    if (!currentUserId) return;

    const targetSessionIds = sessionIds && sessionIds.length > 0
      ? Array.from(new Set(sessionIds.map((id) => String(id || '').trim()).filter(Boolean)))
      : sessions.map((session) => session.id);

    if (targetSessionIds.length === 0) return;

    try {
      const unreadMap = await getUnreadCountsBySessions(currentUserId, targetSessionIds);
      setUnreadBySession((prev) => ({ ...prev, ...(unreadMap || {}) }));
    } catch (error) {
      console.warn('Failed to refresh unread counts:', error);
    }
  }, [currentUserId, sessions]);

  const sendSocketEvent = useCallback((payload, sessionIdOverride = null) => {
    const socket = wsRef.current;
    const sessionId = sessionIdOverride || activeSessionId;
    if (!socket || socket.readyState !== WebSocket.OPEN || !sessionId) return;
    socket.send(
      JSON.stringify({
        ...payload,
        session_id: sessionId,
      }),
    );
  }, [activeSessionId]);

  const sendTypingEvent = useCallback((type) => {
    sendSocketEvent({ type });
  }, [sendSocketEvent]);

  const setActiveSession = useCallback((nextSessionId) => {
    const previousSessionId = String(activeSessionIdRef.current || '').trim();
    const normalizedNextSessionId = String(nextSessionId || '').trim();
    if (previousSessionId && previousSessionId !== normalizedNextSessionId) {
      scheduleReadMark(previousSessionId, true);
    }
    if (previousSessionId !== normalizedNextSessionId) {
      setTypingText('');
      setMessages([]);
      setIsMessagesLoading(Boolean(normalizedNextSessionId));
    }
    activeSessionIdRef.current = nextSessionId;
    setActiveSessionId(nextSessionId);
  }, [scheduleReadMark]);

  const toggleSidebarSection = useCallback((sectionKey) => {
    setExpandedSidebarSection((prev) => (prev === sectionKey ? null : sectionKey));
  }, []);

  useEffect(() => {
    const pending = pendingPanelFocusRef.current;
    if (!pending?.sessionId) return;
    if (!combinedSessions.some((session) => session.id === pending.sessionId)) return;
    if (pending.sectionKey) {
      setExpandedSidebarSection(pending.sectionKey);
    }
    setActiveSession(pending.sessionId);
    setUnreadBySession((prev) => ({ ...prev, [pending.sessionId]: 0 }));
    pendingPanelFocusRef.current = null;
  }, [combinedSessions, setActiveSession]);

  const applyIncomingMessage = useCallback((incomingMessage) => {
    if (!incomingMessage?.sessionId) return;
    const isActiveNow = activeSessionIdRef.current === incomingMessage.sessionId;

    const nextSessionMessages = updateSessionCache(incomingMessage.sessionId, (current) => {
      const existsById = current.some((msg) => msg.id === incomingMessage.id);
      if (existsById) return current;

      const duplicateIndex = current.findIndex(
        (msg) =>
          msg.sender === incomingMessage.sender &&
          msg.text === incomingMessage.text &&
          Math.abs(msg.at - incomingMessage.at) <= 1200,
      );
      if (duplicateIndex >= 0) return current;

      const replaceIndex = current.findIndex(
        (msg) =>
          msg.sender === incomingMessage.sender &&
          msg.text === incomingMessage.text &&
          (msg.pending || msg.failed) &&
          Math.abs(msg.at - incomingMessage.at) <= 10000,
      );

      if (replaceIndex >= 0) {
        const next = [...current];
        next[replaceIndex] = {
          ...next[replaceIndex],
          ...incomingMessage,
          pending: false,
          failed: false,
        };
        return next.sort((a, b) => a.at - b.at);
      }

      return [...current, incomingMessage].sort((a, b) => a.at - b.at);
    });

    if (isActiveNow) {
      setMessages(nextSessionMessages);
    }

    updateRegularSessionLists((prev) =>
      prev.map((session) =>
        session.id === incomingMessage.sessionId
          ? {
              ...session,
              preview: incomingMessage.text,
              updatedAt: incomingMessage.at,
            }
          : session,
      ),
    );

    if (incomingMessage.sender !== currentUserId) {
      if (isActiveNow && isOpen) {
        setUnreadBySession((prev) => ({ ...prev, [incomingMessage.sessionId]: 0 }));
        scheduleReadMark(incomingMessage.sessionId, true, new Date(incomingMessage.at).toISOString());
      } else {
        setUnreadBySession((prev) => ({
          ...prev,
          [incomingMessage.sessionId]: (prev[incomingMessage.sessionId] || 0) + 1,
        }));
      }
    }
  }, [currentUserId, isOpen, updateSessionCache, scheduleReadMark, updateRegularSessionLists]);

  useEffect(() => {
    if (!currentUserId) return undefined;
    loadSessions();
    loadTutorStudentSessions();
    loadAiSessions();
    return undefined;
  }, [currentUserId, loadSessions, loadTutorStudentSessions, loadAiSessions]);

  useEffect(() => {
    if (!isOpen || !currentUserId) return undefined;
    // Refresh once on open to avoid stale per-session unread dots.
    void loadSessions();
    void loadTutorStudentSessions();
    void loadAiSessions();
    void loadAvailableAiModels();
    return undefined;
  }, [isOpen, currentUserId, loadSessions, loadTutorStudentSessions, loadAiSessions, loadAvailableAiModels]);

  useEffect(() => {
    if (!isOpen || !currentUserId) return undefined;

    const intervalId = window.setInterval(() => {
      void loadSessions();
      void loadTutorStudentSessions({ refreshUnread: false });
    }, CHAT_PANEL_REFRESH_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isOpen, currentUserId, loadSessions, loadTutorStudentSessions]);

  useEffect(() => {
    if (!isOpen || !currentUserId) return undefined;
    void loadAvailableAiModels();
    return undefined;
  }, [currentUserId, isOpen, loadAvailableAiModels]);

  useEffect(() => {
    if (!isActiveAiSession) {
      setIsAiModelMenuOpen(false);
    }
  }, [isActiveAiSession, activeSessionId]);

  useEffect(() => {
    if (!isPickerOpen) return undefined;

    const handlePointerDownOutside = (event) => {
      const target = event.target;
      if (dmPickerRef.current?.contains(target) || dmPickerToggleRef.current?.contains(target)) {
        return;
      }
      setIsPickerOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDownOutside);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside);
    };
  }, [isPickerOpen]);

  useEffect(() => {
    if (aiModels.length !== 1) return;
    const [defaultModel] = aiModels;
    if (!defaultModel?.modelId) return;
    if (aiSessions.length === 0) return;

    setAiSessions((prev) => {
      let hasChanged = false;
      const next = prev.map((session) => {
        if (session.chatKind !== 'ai') return session;
        if (String(session.currentModelId || '').trim()) return session;

        hasChanged = true;
        return {
          ...session,
          currentModelId: defaultModel.modelId,
          currentModelName: defaultModel.displayName,
          currentModelProvider: defaultModel.provider,
          peerAvatar: defaultModel.icon || session.peerAvatar || '',
        };
      });

      return hasChanged ? next : prev;
    });
  }, [aiModels, aiSessions]);

  useEffect(() => {
    if (!isAiModelMenuOpen) return undefined;

    const handlePointerDownOutside = (event) => {
      if (aiModelMenuRef.current && !aiModelMenuRef.current.contains(event.target)) {
        setIsAiModelMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDownOutside);
    return () => document.removeEventListener('mousedown', handlePointerDownOutside);
  }, [isAiModelMenuOpen]);

  useEffect(() => {
    if (!isOpen || !currentUserId) return undefined;
    loadFriends();
    return undefined;
  }, [isOpen, currentUserId, loadFriends]);

  useEffect(() => {
    if (!isOpen || !currentUserId) return undefined;
    void loadTutorStudentSessions();
    return undefined;
  }, [isOpen, currentUserId, loadTutorStudentSessions]);

  useEffect(() => {
    const handleOpenChatPanel = (event) => {
      const detail = event?.detail || {};
      const sectionKey = String(detail.sectionKey || '').trim();
      const sessionId = String(detail.sessionId || '').trim();
      pendingPanelFocusRef.current = { sectionKey, sessionId };
      if (sectionKey) {
        setExpandedSidebarSection(sectionKey);
      }
      if (sessionId) {
        setMessages([]);
        setIsMessagesLoading(true);
        activeSessionIdRef.current = sessionId;
        setActiveSessionId(sessionId);
      }
    };

    window.addEventListener('theralingua:open-chat-panel', handleOpenChatPanel);
    return () => {
      window.removeEventListener('theralingua:open-chat-panel', handleOpenChatPanel);
    };
  }, []);

  useEffect(() => {
    if (!toast.text) return undefined;
    const timer = setTimeout(() => {
      setToast({ text: '', type: '' });
    }, 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!isActiveAiSession) {
      setEditingAiSessionTitle(false);
      setAiSessionTitleInput('');
      return;
    }

    if (!editingAiSessionTitle) {
      setAiSessionTitleInput(activeSession?.name || '');
    }
  }, [isActiveAiSession, activeSession?.id, activeSession?.name, editingAiSessionTitle]);

  useEffect(() => {
    if (!aiComposerEditContext) return;
    if (!activeSession || activeSession.chatKind !== 'ai' || activeSession.id !== aiComposerEditContext.sessionId) {
      setAiComposerEditContext(null);
    }
  }, [activeSession, aiComposerEditContext]);

  useEffect(() => {
    if (!isOpen) return;
    if (!currentUserId) {
      console.warn('ChatHubPanel: currentUserId is empty, session list cannot be loaded.');
    }
  }, [isOpen, currentUserId]);

  useEffect(() => {
    if (!currentUserId || !activeSessionId) {
      setIsMessagesLoading(false);
      setMessages([]);
      return;
    }
    if (!isOpen) return;
    if (!activeSession) {
      setIsMessagesLoading(true);
      setMessages([]);
      return;
    }

    if (activeSession?.chatKind === 'ai') {
      setIsMessagesLoading(true);
      const cached = messageCacheRef.current[activeSessionId];
      if (cached?.length) {
        setMessages(cached);
      } else {
        setMessages([]);
      }

      let cancelled = false;
      (async () => {
        try {
          const rows = await getAiChatMessages(activeSessionId, currentUserId);
          if (cancelled) return;
          const merged = applySessionMessages(activeSessionId, (current) => {
            const suppressedUserSeqs = aiSuppressedUserSeqsRef.current[activeSessionId] || new Set();
            const suppressedSystemSeqs = aiSuppressedSystemSeqsRef.current[activeSessionId] || new Set();
            const serverRows = (rows || []).filter((row) => {
              if (row.kind === 'system_error') {
                const rowSeq = Number.isFinite(row.messageSeq) ? Number(row.messageSeq) : Number(row.id);
                if (!Number.isFinite(rowSeq) || rowSeq <= 0) return true;
                return !suppressedSystemSeqs.has(rowSeq);
              }
              if (row.sender !== currentUserId) return true;
              const rowSeq = Number.isFinite(row.messageSeq) ? Number(row.messageSeq) : Number(row.id);
              if (!Number.isFinite(rowSeq) || rowSeq <= 0) return true;
              return !suppressedUserSeqs.has(rowSeq);
            });
            const now = Date.now();
            const preservedClientMsgs = current.filter((msg) => {
              if (msg.sessionId !== activeSessionId) return false;
              if (msg.kind === 'system_error') {
                const hasEquivalentSystemError = serverRows.some((row) => {
                  if (row.kind !== 'system_error') return false;
                  if (msg.messageSeq && row.messageSeq && Number(msg.messageSeq) === Number(row.messageSeq)) {
                    return true;
                  }
                  return row.text === msg.text && Math.abs(row.at - msg.at) <= 10000;
                });
                return !hasEquivalentSystemError;
              }
              if (serverRows.some((row) => row.id === msg.id)) return false;

              const hasDbEquivalent = serverRows.some(
                (row) =>
                  row.sender === msg.sender &&
                  row.text === msg.text &&
                  Math.abs(row.at - msg.at) <= 10000,
              );
              if (hasDbEquivalent) return false;
              if (msg.sender === 'assistant' && msg.clientTempId) {
                if (msg.failed) return true;
                const streamState = aiStreamStateRef.current[msg.clientTempId];
                return Boolean(msg.pending && streamState?.sessionId === activeSessionId);
              }
              if (msg.pending || msg.failed) return true;
              return now - msg.at <= MESSAGE_CLIENT_FALLBACK_KEEP_MS;
            });

            const mergedRows = [...serverRows];
            for (const msg of preservedClientMsgs) {
              if (!mergedRows.some((row) => row.id === msg.id)) {
                mergedRows.push(msg);
              }
            }
            return mergedRows.sort((a, b) => a.at - b.at);
          });
          if (!cancelled) {
            setMessages(merged);
            setIsMessagesLoading(false);
          }
        } catch (error) {
          console.error('Failed to load ai chat messages:', error);
          if (!cancelled) {
            if (!cached?.length) setMessages([]);
            setIsMessagesLoading(false);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    if (activeSession?.isDraft) {
      setIsMessagesLoading(false);
      setMessages(activeSession.localMessages || []);
      return;
    }

    setIsMessagesLoading(true);
    const cached = messageCacheRef.current[activeSessionId];
    if (cached?.length) {
      setMessages(cached);
    }
    setUnreadBySession((prev) => ({ ...prev, [activeSessionId]: 0 }));

    let cancelled = false;
    (async () => {
      try {
        const rows = activeSession?.chatKind === 'tutor_student'
          ? await getTutorStudentChatMessages(activeSessionId, currentUserId)
          : await getChatMessages(activeSessionId, currentUserId);
        if (!cancelled) {
          setMessages((prev) => {
            const now = Date.now();
            const dbIdSet = new Set((rows || []).map((msg) => msg.id));
            const preservedClientMsgs = prev.filter((msg) => {
              if (msg.sessionId !== activeSessionId) return false;
              if (dbIdSet.has(msg.id)) return false;
              const hasDbEquivalent = (rows || []).some(
                (row) =>
                  row.sender === msg.sender &&
                  row.text === msg.text &&
                  Math.abs(row.at - msg.at) <= 5000,
              );
              if (hasDbEquivalent) return false;
              if (msg.pending || msg.failed) return true;
              // Keep short-lived websocket-only messages until DB catches up.
              return now - msg.at <= MESSAGE_CLIENT_FALLBACK_KEEP_MS;
            });

            const merged = [...(rows || [])];
            for (const msg of preservedClientMsgs) {
              if (!merged.some((row) => row.id === msg.id)) {
                merged.push(msg);
              }
            }
            return merged.sort((a, b) => a.at - b.at);
          });
          const latestServerMessageAt = (rows || []).reduce((latest, row) => {
            const rowAt = Number(row?.at || 0);
            return rowAt > latest ? rowAt : latest;
          }, 0);
          if (latestServerMessageAt > 0 && activeSessionIdRef.current === activeSessionId) {
            scheduleReadMark(activeSessionId, true, new Date(latestServerMessageAt).toISOString());
          }
          setIsMessagesLoading(false);
        }
      } catch (error) {
        console.error('Failed to load chat messages:', error);
        if (!cancelled) {
          if (!cached?.length) setMessages([]);
          setIsMessagesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, currentUserId, activeSessionId, activeSession?.chatKind, activeSession?.isDraft, activeSession?.localMessages, applySessionMessages, scheduleReadMark]);

  useEffect(() => {
    if (!activeSessionId || activeSession?.isDraft || activeSession?.chatKind === 'ai') return;
    updateSessionCache(activeSessionId, () => messages);
  }, [activeSessionId, activeSession?.chatKind, activeSession?.isDraft, messages, updateSessionCache]);

  useEffect(() => () => {
    clearAiStreamPlayback();
  }, [clearAiStreamPlayback]);

  useEffect(() => {
    if (!isOpen || !currentUserId || !activeSession || activeSession.isDraft || activeSession.chatKind !== 'ai') {
      clearAiStreamPlayback();
      closeAiSocket();
      return undefined;
    }

    let disposed = false;
    let socket = null;

    (async () => {
      try {
        socket = await ensureAiSocketReady(activeSession.id);
        if (disposed || !socket) return;
      } catch (error) {
        console.error('Failed to prepare AI websocket:', error);
      }
    })();

    return () => {
      disposed = true;
    };
  }, [
    isOpen,
    currentUserId,
    activeSession,
    ensureAiSocketReady,
    closeAiSocket,
    clearAiStreamPlayback,
  ]);

  const lastMessageTextLength = messages[messages.length - 1]?.text?.length || 0;

  useLayoutEffect(() => {
    if (!isOpen || !activeSessionId) return;
    scrollMessagesToBottom('auto');
  }, [isOpen, activeSessionId, messages.length, lastMessageTextLength, scrollMessagesToBottom]);

  useEffect(() => {
    if (!isOpen || !currentUserId || !activeSession || activeSession.isDraft || activeSession.chatKind === 'ai') {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setTypingText('');
      return undefined;
    }

    const wsUrls = buildWsUrls(activeSession.id, currentUserId);
    if (wsUrls.length === 0) return undefined;
    const currentSessionId = activeSession.id;
    const currentSessionName = activeSession.name;
    let disposed = false;
    let reconnectTimer = null;
    let reconnectAttempt = 0;

    const clearReconnect = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const connect = (candidateIndex = 0) => {
      if (disposed) return;
      const wsUrl = wsUrls[Math.min(candidateIndex, wsUrls.length - 1)];
      let opened = false;
      const socket = new WebSocket(wsUrl);
      wsRef.current = socket;

      socket.onopen = () => {
        opened = true;
        reconnectAttempt = 0;
      };

      socket.onmessage = (event) => {
        try {
          const payload = normalizeRealtimePayload(JSON.parse(event.data || '{}'));
          if (!payload || payload.sessionId !== currentSessionId) return;
          if (!payload.userId || payload.userId === currentUserId) return;

          if (
            payload.type === 'typing_start'
            || payload.type === 'user_typing_start'
            || (payload.type === 'typing' && payload.isTyping)
          ) {
            setTypingText(`${currentSessionName} is typing...`);
          } else if (
            payload.type === 'typing_stop'
            || payload.type === 'user_typing_stop'
            || (payload.type === 'typing' && !payload.isTyping)
          ) {
            setTypingText('');
          } else if (payload.type === 'message_new' && payload.content) {
            const incomingText = payload.content;
            const incomingAt = payload.serverTs;
            const incomingId = payload.tempId || `ws-${incomingAt}`;

            applyIncomingMessage({
              id: incomingId,
              legacyId: null,
              messageSeq: null,
              sessionId: currentSessionId,
              sender: payload.userId,
              role: payload.userId === currentUserId ? 'me' : 'other',
              text: incomingText,
              at: incomingAt,
              pending: false,
              failed: false,
            });
          }
        } catch {
          // Ignore malformed payloads.
        }
      };

      socket.onclose = () => {
        if (wsRef.current === socket) wsRef.current = null;
        if (disposed) return;
        if (!opened && candidateIndex + 1 < wsUrls.length) {
          connect(candidateIndex + 1);
          return;
        }
        reconnectAttempt += 1;
        const delayMs = Math.min(4000, 600 + reconnectAttempt * 300);
        clearReconnect();
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null;
          connect();
        }, delayMs);
      };
    };

    connect();

    return () => {
      disposed = true;
      clearReconnect();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isOpen, currentUserId, activeSessionId, activeSession?.chatKind, activeSession?.isDraft, activeSession?.name, applyIncomingMessage]);

  useEffect(() => {
    if (!isOpen || !currentUserId || !activeSession || activeSession.isDraft || activeSession.chatKind === 'ai') return undefined;
    const currentSessionId = activeSession.id;
    const tableName = activeSession.chatKind === 'tutor_student' ? 'tutor_student_chat' : 'private_chat';

    const channel = supabase
      .channel(`chat-backup-${currentSessionId}-${currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: tableName,
          filter: `session_id=eq.${currentSessionId}`,
        },
        (payload) => {
          const row = payload?.new;
          if (!row || !row.message) return;

          // Avoid duplicate rendering when websocket has already delivered this message.
          const socketOpen = wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
          if (socketOpen) return;

          const incomingAt = row.sent_at ? new Date(row.sent_at).getTime() : Date.now();
          applyIncomingMessage({
            id: row.message_seq,
            legacyId: null,
            messageSeq: row.message_seq ?? null,
            sessionId: row.session_id,
            sender: row.sender,
            role: row.sender === currentUserId ? 'me' : 'other',
            text: row.message,
            at: incomingAt,
            pending: false,
            failed: false,
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isOpen, currentUserId, activeSessionId, activeSession?.isDraft, activeSession?.chatKind, applyIncomingMessage]);

  useEffect(() => {
    if (!currentUserId) return undefined;

    const channel = supabase
      .channel(`chat-unread-live-${currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'private_chat',
        },
        async (payload) => {
          const row = payload?.new;
          if (!row?.session_id) return;
          if (!shouldProcessRealtimeInsert('private_chat', row)) return;

          const sessionId = String(row.session_id);
          const senderId = String(row.sender || '').toLowerCase();
          const meId = currentUserId.toLowerCase();
          const isMine = senderId === meId;
          const isActiveNow = activeSessionIdRef.current === sessionId;

          let hasSession = false;
          setSessions((prev) => {
            hasSession = prev.some((session) => session.id === sessionId);
            if (!hasSession) return prev;
            return prev.map((session) =>
              session.id === sessionId
                ? {
                    ...session,
                    preview: row.message || session.preview,
                    updatedAt: row.sent_at ? new Date(row.sent_at).getTime() : Date.now(),
                  }
                : session,
            );
          });

          if (!hasSession) {
            await loadSessions();
          }

          if (!isMine) {
            if (isOpen && isActiveNow) {
              setUnreadBySession((prev) => ({ ...prev, [sessionId]: 0 }));
              scheduleReadMark(sessionId, true, row.sent_at || new Date().toISOString());
            } else {
              setUnreadBySession((prev) => ({
                ...prev,
                [sessionId]: (prev[sessionId] || 0) + 1,
              }));
            }
          }
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tutor_student_chat',
        },
        async (payload) => {
          const row = payload?.new;
          if (!row?.session_id) return;
          if (!shouldProcessRealtimeInsert('tutor_student_chat', row)) return;

          const sessionId = String(row.session_id);
          const senderId = String(row.sender || '').toLowerCase();
          const meId = currentUserId.toLowerCase();
          const isMine = senderId === meId;
          const isActiveNow = activeSessionIdRef.current === sessionId;

          let hasSession = false;
          updateRegularSessionLists((prev) => {
            const sessionExists = prev.some((session) => session.id === sessionId);
            hasSession = hasSession || sessionExists;
            if (!sessionExists) return prev;
            return prev.map((session) =>
              session.id === sessionId
                ? {
                    ...session,
                    preview: row.message || session.preview,
                    updatedAt: row.sent_at ? new Date(row.sent_at).getTime() : Date.now(),
                  }
                : session,
            );
          });

          if (!hasSession) {
            await loadTutorStudentSessions();
          }

          if (!isMine) {
            if (isOpen && isActiveNow) {
              setUnreadBySession((prev) => ({ ...prev, [sessionId]: 0 }));
              scheduleReadMark(sessionId, true, row.sent_at || new Date().toISOString());
            } else {
              setUnreadBySession((prev) => ({
                ...prev,
                [sessionId]: (prev[sessionId] || 0) + 1,
              }));
            }
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isOpen, currentUserId, loadSessions, loadTutorStudentSessions, scheduleReadMark, shouldProcessRealtimeInsert, updateRegularSessionLists]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (chatViewMode === 'focus') {
        setChatViewMode('docked');
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, chatViewMode]);

  useEffect(() => {
    const shouldLockBackgroundScroll = isOpen && chatViewMode === 'focus';
    document.documentElement.classList.toggle('chat-hub-focus-lock', shouldLockBackgroundScroll);
    document.body.classList.toggle('chat-hub-focus-lock', shouldLockBackgroundScroll);

    return () => {
      document.documentElement.classList.remove('chat-hub-focus-lock');
      document.body.classList.remove('chat-hub-focus-lock');
    };
  }, [isOpen, chatViewMode]);

  useEffect(() => {
    if (!isOpen) {
      setChatViewMode('docked');
      setTypingText('');
      if (activeSessionIdRef.current) {
        scheduleReadMark(activeSessionIdRef.current, true);
      }
    }
  }, [isOpen, scheduleReadMark]);

  useEffect(() => {
    const textarea = composerRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const styles = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(styles.lineHeight) || 20;
    const paddingTop = parseFloat(styles.paddingTop) || 0;
    const paddingBottom = parseFloat(styles.paddingBottom) || 0;
    const borderTop = parseFloat(styles.borderTopWidth) || 0;
    const borderBottom = parseFloat(styles.borderBottomWidth) || 0;
    const maxHeight = lineHeight * COMPOSER_MAX_LINES + paddingTop + paddingBottom + borderTop + borderBottom;
    const nextHeight = Math.min(textarea.scrollHeight, maxHeight);

    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    textarea.scrollTop = textarea.scrollHeight;
  }, [composerText, isOpen]);

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      if (readMarkTimerRef.current) clearTimeout(readMarkTimerRef.current);
      if (activeSessionIdRef.current) {
        scheduleReadMark(activeSessionIdRef.current, true);
      }
      void flushReadMarks();
      if (wsRef.current) wsRef.current.close();
    };
  }, [flushReadMarks, scheduleReadMark]);

  const handleCreateDraft = (friend) => {
    const existing = sessions.find(
      (session) => session.targetUserId === friend.id || session.name === friend.username,
    );

    if (existing) {
      setActiveSession(existing.id);
      setUnreadBySession((prev) => ({ ...prev, [existing.id]: 0 }));
      setIsPickerOpen(false);
      return;
    }

    const draftId = `draft-${friend.id}`;
    const draftSession = {
      id: draftId,
      chatKind: 'dm',
      targetUserId: friend.id,
      name: friend.username || friend.id,
      peerAvatar: friend.avatar || '',
      subtitle: 'DM Draft',
      preview: 'No messages yet',
      updatedAt: Date.now(),
      isDraft: true,
      localMessages: [],
    };
    setSessions((prev) => [draftSession, ...prev]);
    updateSessionCache(draftId, () => []);
    setActiveSession(draftId);
    setUnreadBySession((prev) => ({ ...prev, [draftId]: 0 }));
    setIsPickerOpen(false);
  };

  const handleDmCreateClick = useCallback(() => {
    setExpandedSidebarSection('dm');
    setIsPickerOpen((prev) => !prev);
    setIsAiCreatePanelOpen(false);
    setAiCreateNotice('');
  }, []);

  const handleAiCreateClick = useCallback(() => {
    setExpandedSidebarSection('ai');
    setIsPickerOpen(false);
    setAiCreateNotice('');
    setIsAiCreatePanelOpen((prev) => !prev);
  }, []);

  const handleCreateAiSession = useCallback(async () => {
    if (isCreatingAiSession) return;
    const previousActiveSessionId = activeSessionIdRef.current;
    const tempSessionId = `temp-ai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimisticCreatedAt = Date.now();
    const normalizedLanguage = String(aiSessionLanguage || '').trim();
    const selectedLanguageLabel = practiceLanguages.find((item) => item.value === normalizedLanguage)?.label || '';
    const optimisticSession = {
      id: tempSessionId,
      type: 'ai_chat',
      chatKind: 'ai',
      targetUserId: '',
      name: 'New Chat',
      peerAvatar: '',
      subtitle: selectedLanguageLabel || 'AI',
      preview: 'Creating session...',
      updatedAt: optimisticCreatedAt,
      isDraft: false,
      isCreating: true,
      currentModelId: '',
      currentModelName: 'Preparing session...',
      language: normalizedLanguage || null,
      languageLabel: selectedLanguageLabel || '',
    };

    setExpandedSidebarSection('ai');
    setIsPickerOpen(false);
    setIsAiCreatePanelOpen(false);
    setAiCreateNotice('');
    setIsCreatingAiSession(true);
    setAiSessions((prev) => [optimisticSession, ...prev]);
    setActiveSession(tempSessionId);
    setMessages([]);

    try {
      const created = await createAiChatSession(normalizedLanguage || null);
      const newSessionId = String(created?.session_id || '').trim();
      if (!newSessionId) {
        throw new Error('Failed to create ai chat session.');
      }

      const sessionName = String(created?.ai_chat_session?.session_name || 'New Chat').trim() || 'New Chat';
      const createdAtRaw = created?.session?.created_at || new Date().toISOString();
      const committedSession = {
        id: newSessionId,
        type: 'ai_chat',
        chatKind: 'ai',
        targetUserId: '',
        name: sessionName,
        peerAvatar: '',
        subtitle: selectedLanguageLabel || 'AI',
        preview: 'No messages yet',
        updatedAt: createdAtRaw ? new Date(createdAtRaw).getTime() : Date.now(),
        isDraft: false,
        isCreating: false,
        currentModelId: '',
        currentModelName: 'No model selected',
        language: normalizedLanguage || null,
        languageLabel: selectedLanguageLabel || '',
      };

      setAiSessions((prev) => {
        const next = [
          committedSession,
          ...prev.filter((session) => session.id !== tempSessionId && session.id !== newSessionId),
        ];
        return next.sort((a, b) => b.updatedAt - a.updatedAt);
      });
      setActiveSession(newSessionId);
      setMessages([]);
      void loadAiSessions();
    } catch (error) {
      console.error('Failed to create ai chat session:', error);
      setAiSessions((prev) => prev.filter((session) => session.id !== tempSessionId));
      if (activeSessionIdRef.current === tempSessionId) {
        setActiveSession(previousActiveSessionId || null);
      }
      setAiCreateNotice(error instanceof Error ? error.message : 'Failed to create ai chat session.');
    } finally {
      setIsCreatingAiSession(false);
    }
  }, [aiSessionLanguage, isCreatingAiSession, loadAiSessions, practiceLanguages, setActiveSession]);

  const handleDeleteAiSession = useCallback(async () => {
    if (!activeSession || activeSession.chatKind !== 'ai' || activeSession.isCreating || isDeletingAiSession) return;

    const targetSessionId = activeSession.id;
    const targetSessionName = activeSession.name || 'AI chat';

    try {
      setIsDeletingAiSession(true);
      closeAiSocket();
      clearAiStreamPlayback();
      await deleteAiChatSession(targetSessionId);
      setAiSessions((prev) => prev.filter((session) => session.id !== targetSessionId));
      updateSessionCache(targetSessionId, () => []);
      if (activeSessionIdRef.current === targetSessionId) {
        setActiveSession(null);
        setMessages([]);
      }
      setAiCreateNotice('');
      setToast({ text: `"${targetSessionName}" deleted.`, type: 'success' });
    } catch (error) {
      console.error('Failed to delete ai chat session:', error);
      setAiCreateNotice(error instanceof Error ? error.message : 'Failed to delete ai chat session.');
      setToast({ text: error instanceof Error ? error.message : 'Failed to delete AI chat.', type: 'error' });
    } finally {
      setIsDeletingAiSession(false);
      setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
    }
  }, [activeSession, clearAiStreamPlayback, closeAiSocket, isDeletingAiSession, setActiveSession, updateSessionCache]);

  const handleOpenDeleteAiConfirm = useCallback(() => {
    if (!activeSession || activeSession.chatKind !== 'ai' || activeSession.isCreating || isDeletingAiSession) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Delete AI Chat',
      message: `Delete "${activeSession.name || 'this AI chat'}"? This will permanently remove the full AI session history.`,
      onConfirm: handleDeleteAiSession,
    });
  }, [activeSession, handleDeleteAiSession, isDeletingAiSession]);

  const handleStartEditAiSessionTitle = useCallback(() => {
    if (!activeSession || activeSession.chatKind !== 'ai' || isRenamingAiSession) return;
    setAiSessionTitleInput(activeSession.name || '');
    setEditingAiSessionTitle(true);
  }, [activeSession, isRenamingAiSession]);

  const handleCancelEditAiSessionTitle = useCallback(() => {
    setEditingAiSessionTitle(false);
    setAiSessionTitleInput(activeSession?.name || '');
  }, [activeSession?.name]);

  const handleSubmitAiSessionTitle = useCallback(async () => {
    if (!activeSession || activeSession.chatKind !== 'ai' || isRenamingAiSession) return;

    const nextTitle = String(aiSessionTitleInput || '').trim().replace(/\s+/g, ' ');
    const currentTitle = String(activeSession.name || '').trim();

    if (!nextTitle) {
      setToast({ text: 'Title cannot be empty.', type: 'error' });
      return;
    }

    if (nextTitle.length > 60) {
      setToast({ text: 'Title must be 60 characters or fewer.', type: 'error' });
      return;
    }

    if (nextTitle === currentTitle) {
      setEditingAiSessionTitle(false);
      return;
    }

    const targetSessionId = activeSession.id;

    setIsRenamingAiSession(true);
    setAiSessions((prev) =>
      prev.map((session) =>
        session.id === targetSessionId
          ? {
              ...session,
              name: nextTitle,
            }
          : session,
      ),
    );

    try {
      await updateAiChatSessionName(targetSessionId, nextTitle);
      setEditingAiSessionTitle(false);
      setToast({ text: 'Chat title updated.', type: 'success' });
    } catch (error) {
      setAiSessions((prev) =>
        prev.map((session) =>
          session.id === targetSessionId
            ? {
                ...session,
                name: currentTitle,
              }
            : session,
        ),
      );
      setAiSessionTitleInput(currentTitle);
      setToast({ text: error instanceof Error ? error.message : 'Failed to update chat title.', type: 'error' });
    } finally {
      setIsRenamingAiSession(false);
    }
  }, [activeSession, aiSessionTitleInput, isRenamingAiSession]);

  const handleSelectAiModel = useCallback((model) => {
    if (!model?.modelId || !activeSessionIdRef.current) return;
    const targetSessionId = activeSessionIdRef.current;
    setAiSessions((prev) =>
      prev.map((session) =>
        session.id === targetSessionId
          ? {
              ...session,
              currentModelId: model.modelId,
              currentModelName: model.displayName,
              currentModelProvider: model.provider,
              peerAvatar: model.icon || session.peerAvatar || '',
            }
          : session,
      ),
    );
    setIsAiModelMenuOpen(false);
  }, []);

  const sendAiMessage = useCallback(async ({
    session,
    text,
    reuseClientTempId = '',
    replaceExisting = false,
    relatedErrorId = '',
    relatedUserMessageSeq = null,
  }) => {
    if (!session || session.chatKind !== 'ai') return false;

    const normalizedText = String(text || '').trim();
    const currentModelId = String(session.currentModelId || '').trim();
    if (!normalizedText || !currentModelId) return false;

    const tempId = String(reuseClientTempId || makeLocalId()).trim();
    const now = Date.now();
    const optimisticMessage = {
      id: tempId,
      legacyId: null,
      messageSeq: null,
      sessionId: session.id,
      sender: currentUserId,
      text: normalizedText,
      at: now,
      role: 'me',
      pending: true,
      failed: false,
      modelId: currentModelId,
      clientTempId: tempId,
    };

    clearAiPendingAssistant(session.id, tempId);
    removeAiSystemErrorMessages(session.id, {
      errorId: relatedErrorId,
      relatedUserClientTempId: tempId,
      relatedUserMessageSeq,
    });

    if (replaceExisting) {
      applySessionMessages(session.id, (current) => {
        let replaced = false;
        const nextMessages = current.map((msg) => {
          if (msg.id !== tempId) return msg;
          replaced = true;
          return {
            ...msg,
            ...optimisticMessage,
          };
        });
        return (replaced ? nextMessages : [...nextMessages, optimisticMessage]).sort((a, b) => a.at - b.at);
      });
    } else {
      setMessages((prev) => [...prev, optimisticMessage]);
      updateSessionCache(session.id, (current) => [...current, optimisticMessage].sort((a, b) => a.at - b.at));
    }

    setAiSessions((prev) =>
      prev.map((item) =>
        item.id === session.id
          ? {
              ...item,
              preview: normalizedText,
              updatedAt: now,
            }
          : item,
      ),
    );
    setComposerText('');
    setIsAiSending(true);

    try {
      const socket = await ensureAiSocketReady(session.id);
      socket.send(JSON.stringify({
        type: 'send_message',
        message: normalizedText,
        model_id: currentModelId,
        client_temp_id: tempId,
      }));
      return true;
    } catch (error) {
      console.error('Failed to send AI message:', error);
      setIsAiSending(false);
      applySessionMessages(session.id, (current) =>
        current.map((msg) =>
          msg.id === tempId
            ? {
                ...msg,
                pending: false,
                failed: true,
              }
            : msg,
        ),
      );
      upsertAiSystemErrorMessage(session.id, {
        client_temp_id: tempId,
        code: 'connection_failed',
        error: error instanceof Error ? error.message : 'Failed to connect to the AI gateway.',
        phase: 'preflight',
        retryable: true,
        user_visible_message: 'Unable to reach the AI gateway right now. Check your connection and try again.',
      });
      return false;
    }
  }, [
    applySessionMessages,
    clearAiPendingAssistant,
    currentUserId,
    ensureAiSocketReady,
    removeAiSystemErrorMessages,
    updateSessionCache,
    upsertAiSystemErrorMessage,
  ]);

  const handleEditAiSystemError = useCallback(async (errorMessage) => {
    if (!errorMessage?.sessionId) return;
    const relatedUserMessage = findAiRelatedUserMessage(errorMessage.sessionId, errorMessage);
    if (!relatedUserMessage?.text) return;

    try {
      setComposerText(relatedUserMessage.text);
      setAiComposerEditContext(null);
      focusComposer();
      void clearAiFailedTurn(errorMessage, relatedUserMessage);
    } catch (error) {
      setToast({ text: error instanceof Error ? error.message : 'Failed to prepare message editing.', type: 'error' });
    }
  }, [clearAiFailedTurn, findAiRelatedUserMessage, focusComposer]);

  const handleRetryAiSystemError = useCallback(async (errorMessage) => {
    if (!errorMessage?.sessionId) return;
    const targetSession = aiSessionsRef.current.find((session) => session.id === errorMessage.sessionId);
    if (!targetSession) return;

    const relatedUserMessage = findAiRelatedUserMessage(errorMessage.sessionId, errorMessage);
    if (!relatedUserMessage?.text) return;

    setAiComposerEditContext(null);
    try {
      void clearAiFailedTurn(errorMessage, relatedUserMessage);

      await sendAiMessage({
        session: targetSession,
        text: relatedUserMessage.text,
        relatedErrorId: '',
        relatedUserMessageSeq: null,
      });
    } catch (error) {
      setToast({ text: error instanceof Error ? error.message : 'Failed to retry this AI message.', type: 'error' });
    }
  }, [
    clearAiFailedTurn,
    findAiRelatedUserMessage,
    sendAiMessage,
  ]);

  const handleSend = async () => {
    const text = composerText.trim();
    if (!text || !activeSession) return;
    if (activeSession.chatKind === 'ai') {
      const started = await sendAiMessage({
        session: activeSession,
        text,
        reuseClientTempId: '',
        replaceExisting: false,
        relatedErrorId: '',
        relatedUserMessageSeq: null,
      });

      if (started) {
        setAiComposerEditContext(null);
      }
      return;
    }

    let targetSessionId = activeSession.id;

    if (activeSession.isDraft) {
      if (creatingSessionRef.current) return;
      creatingSessionRef.current = true;
      setIsCreatingSession(true);
      try {
        const created = await createChatSession(activeSession.targetUserId, 'direct_message', '');
        const newSessionId = created?.session_id;
        if (!newSessionId) throw new Error('Failed to create chat session.');

        targetSessionId = newSessionId;
        setSessions((prev) =>
          prev.map((session) =>
            session.id === activeSession.id
              ? { ...session, id: newSessionId, chatKind: 'dm', isDraft: false, subtitle: 'DM' }
              : session,
          ),
        );
        const draftMessages = messageCacheRef.current[activeSession.id] || [];
        if (draftMessages.length > 0) {
          updateSessionCache(newSessionId, () => draftMessages);
        } else {
          updateSessionCache(newSessionId, () => []);
        }
        setUnreadBySession((prev) => {
          const next = { ...prev, [newSessionId]: prev[activeSession.id] || 0 };
          delete next[activeSession.id];
          return next;
        });
        setActiveSession(newSessionId);
      } catch (error) {
        console.error('Failed to create chat session:', error);
        return;
      } finally {
        creatingSessionRef.current = false;
        setIsCreatingSession(false);
      }
    }

    const tempId = makeLocalId();
    const optimisticMessage = {
      id: tempId,
      sessionId: targetSessionId,
      sender: currentUserId,
      role: 'me',
      text,
      at: Date.now(),
      pending: true,
      failed: false,
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    updateSessionCache(targetSessionId, (current) => [...current, optimisticMessage].sort((a, b) => a.at - b.at));
    scheduleReadMark(targetSessionId);
    updateRegularSessionLists((prev) =>
      prev.map((session) => {
        if (session.id !== targetSessionId && session.id !== activeSession.id) return session;
        return {
          ...session,
          isDraft: false,
          subtitle: session.chatKind === 'tutor_student' ? session.subtitle : 'DM',
          preview: text,
          updatedAt: Date.now(),
        };
      }),
    );

    setComposerText('');
    setTypingText('');

    if (typingSentRef.current) {
      sendTypingEvent('typing_stop');
      typingSentRef.current = false;
    }
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }

    try {
      sendSocketEvent(
        {
          type: 'message_new',
          content: text,
          temp_id: tempId,
        },
        targetSessionId,
      );

      const saved = await sendChatMessage(targetSessionId, text, tempId);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempId
            ? {
                ...msg,
                id: saved.message_id || msg.id,
                legacyId: null,
                messageSeq: saved.message_seq ?? msg.messageSeq ?? null,
                sessionId: saved.session_id || msg.sessionId,
                at: saved.sent_at ? new Date(saved.sent_at).getTime() : msg.at,
                pending: false,
              }
            : msg,
        ),
      );
      updateSessionCache(targetSessionId, (current) =>
        current.map((msg) =>
          msg.id === tempId
            ? {
                ...msg,
                id: saved.message_id || msg.id,
                legacyId: null,
                messageSeq: saved.message_seq ?? msg.messageSeq ?? null,
                sessionId: saved.session_id || msg.sessionId,
                at: saved.sent_at ? new Date(saved.sent_at).getTime() : msg.at,
                pending: false,
              }
            : msg,
        ),
      );
      updateRegularSessionLists((prev) =>
        prev.map((session) =>
          session.id === targetSessionId
            ? {
                ...session,
                preview: saved.message || text,
                updatedAt: saved.sent_at ? new Date(saved.sent_at).getTime() : Date.now(),
              }
            : session,
        ),
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempId
            ? {
                ...msg,
                pending: false,
                failed: true,
              }
            : msg,
        ),
      );
      updateSessionCache(targetSessionId, (current) =>
        current.map((msg) =>
          msg.id === tempId
            ? {
                ...msg,
                pending: false,
                failed: true,
              }
            : msg,
        ),
      );
    }
  };

  const handleComposerKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    if (event.isComposing) return;
    if (event.shiftKey) return;
    event.preventDefault();
    handleSend();
  };

  const handleComposerChange = (event) => {
    const nextValue = event.target.value;
    setComposerText(nextValue);

    if (!activeSession || activeSession.isDraft) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    if (!nextValue.trim()) {
      if (typingSentRef.current) {
        sendTypingEvent('typing_stop');
        typingSentRef.current = false;
      }
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = null;
      }
      return;
    }

    if (!typingSentRef.current) {
      sendTypingEvent('typing_start');
      typingSentRef.current = true;
    }

    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }

    typingTimerRef.current = setTimeout(() => {
      sendTypingEvent('typing_stop');
      typingSentRef.current = false;
      typingTimerRef.current = null;
    }, TYPING_STOP_DELAY_MS);
  };

  const renderSessionButtons = (sessionList) => (
    <div className="chat-hub-session-list">
      {sessionList.map((session) => (
        <button
          key={session.id}
          type="button"
          className={`chat-hub-session-item ${session.id === activeSessionId ? 'active' : ''}`}
          onClick={() => {
            setActiveSession(session.id);
            setUnreadBySession((prev) => ({ ...prev, [session.id]: 0 }));
          }}
        >
          <div className="chat-hub-session-main">
            <div className="chat-hub-avatar chat-hub-avatar-sm">
              <img
                className="chat-hub-avatar-img"
                src={resolveAvatarUrl(session.peerAvatar)}
                alt={`${session.name} avatar`}
                onError={(event) => {
                  event.currentTarget.classList.add('is-hidden');
                }}
              />
              {(presence?.isUserOnline?.(session.targetUserId) || (session.id === activeSessionId && Boolean(typingText))) && (
                <span
                  className="chat-hub-presence-dot"
                  aria-hidden="true"
                />
              )}
              {session.id !== activeSessionId && (unreadBySession[session.id] || 0) > 0 && (
                <span className="chat-hub-unread-dot chat-hub-unread-dot-on-avatar">
                  {(unreadBySession[session.id] || 0) > 99 ? '99+' : unreadBySession[session.id]}
                </span>
              )}
            </div>
            <div className="chat-hub-session-content">
              <div className="chat-hub-session-top">
                <span className="chat-hub-session-name-wrap">
                  <span className="chat-hub-session-name">{session.name}</span>
                </span>
                <div className="chat-hub-session-meta">
                  <span className="chat-hub-session-time">{formatTime(session.updatedAt)}</span>
                </div>
              </div>
              <div className="chat-hub-session-bottom">
                {session.languageLabel && (
                  <span className="chat-hub-session-language-tag">
                    {session.languageTag || session.languageLabel}
                  </span>
                )}
                <span className={`chat-hub-session-preview ${session.isDraft ? 'draft' : ''}`}>
                  {session.preview || 'No messages yet'}
                </span>
              </div>
            </div>
          </div>
        </button>
      ))}
      {sessionList.length === 0 && (
        <div className="chat-hub-section-empty">No conversations yet.</div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div
      className={`chat-hub-overlay mode-${chatViewMode}`}
      onClick={chatViewMode === 'focus' ? onClose : undefined}
    >
      <ToastStack items={[toast]} />
      <div className={`chat-hub-shell mode-${chatViewMode}`} onClick={(event) => event.stopPropagation()}>
        <aside className="chat-hub-sidebar">
          <div className="chat-hub-sidebar-header">
            <div className="chat-hub-sidebar-title">Messages</div>
          </div>

          <div className={`chat-hub-section ${expandedSidebarSection === 'dm' ? 'expanded' : 'collapsed'}`}>
            <div className="chat-hub-section-header">
              <button
                type="button"
                className="chat-hub-section-toggle"
                onClick={() => toggleSidebarSection('dm')}
                aria-expanded={expandedSidebarSection === 'dm'}
                aria-controls="chat-hub-dm-section"
              >
                <span className="chat-hub-section-title">
                  DM
                  {hasDmUnread && <span className="chat-hub-section-unread-dot" aria-hidden="true" />}
                </span>
              </button>
              <div className="chat-hub-section-actions">
                <button
                  type="button"
                  className="chat-hub-section-action-btn"
                  ref={dmPickerToggleRef}
                  onClick={handleDmCreateClick}
                  title="New DM"
                  aria-label="New DM"
                  aria-expanded={isPickerOpen}
                  aria-controls="chat-hub-dm-picker"
                >
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.1">
                    <path d="M10 4v12M4 10h12" />
                  </svg>
                </button>
                {isPickerOpen && (
                  <div id="chat-hub-dm-picker" ref={dmPickerRef} className="chat-hub-picker chat-hub-picker-popover">
                    <div className="chat-hub-picker-title">Start from Friends</div>
                    {availableDmFriends.map((friend) => (
                      <button
                        key={friend.id}
                        type="button"
                        className="chat-hub-picker-item"
                        onClick={() => handleCreateDraft(friend)}
                      >
                        <span>{friend.username || friend.id}</span>
                        <span className="chat-hub-picker-subtitle">Friend</span>
                      </button>
                    ))}
                    {availableDmFriends.length === 0 && (
                      <div className="chat-hub-picker-empty">No friends available.</div>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="chat-hub-section-action-btn"
                  onClick={() => toggleSidebarSection('dm')}
                  title={expandedSidebarSection === 'dm' ? 'Collapse DM' : 'Expand DM'}
                  aria-label={expandedSidebarSection === 'dm' ? 'Collapse DM' : 'Expand DM'}
                >
                  <span className={`chat-hub-section-chevron ${expandedSidebarSection === 'dm' ? 'expanded' : ''}`} aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.1">
                      <path d="M6 8l4 4 4-4" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
            <div
              id="chat-hub-dm-section"
              className={`chat-hub-section-panel ${expandedSidebarSection === 'dm' ? 'expanded' : 'collapsed'}`}
            >
              {renderSessionButtons(sortedSessions)}
            </div>
          </div>

          <div className={`chat-hub-section ${expandedSidebarSection === 'my_tutors' ? 'expanded' : 'collapsed'}`}>
            <div className="chat-hub-section-header">
              <button
                type="button"
                className="chat-hub-section-toggle"
                onClick={() => toggleSidebarSection('my_tutors')}
                aria-expanded={expandedSidebarSection === 'my_tutors'}
                aria-controls="chat-hub-my-tutors-section"
              >
                <span className="chat-hub-section-title">
                  My Tutors
                  {hasMyTutorUnread && <span className="chat-hub-section-unread-dot" aria-hidden="true" />}
                </span>
              </button>
              <div className="chat-hub-section-actions">
                <button
                  type="button"
                  className="chat-hub-section-action-btn"
                  onClick={() => toggleSidebarSection('my_tutors')}
                  title={expandedSidebarSection === 'my_tutors' ? 'Collapse My Tutors' : 'Expand My Tutors'}
                  aria-label={expandedSidebarSection === 'my_tutors' ? 'Collapse My Tutors' : 'Expand My Tutors'}
                >
                  <span className={`chat-hub-section-chevron ${expandedSidebarSection === 'my_tutors' ? 'expanded' : ''}`} aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.1">
                      <path d="M6 8l4 4 4-4" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
            <div
              id="chat-hub-my-tutors-section"
              className={`chat-hub-section-panel ${expandedSidebarSection === 'my_tutors' ? 'expanded' : 'collapsed'}`}
            >
              {renderSessionButtons(sortedMyTutorSessions)}
            </div>
          </div>

          <div className={`chat-hub-section ${expandedSidebarSection === 'my_students' ? 'expanded' : 'collapsed'}`}>
            <div className="chat-hub-section-header">
              <button
                type="button"
                className="chat-hub-section-toggle"
                onClick={() => toggleSidebarSection('my_students')}
                aria-expanded={expandedSidebarSection === 'my_students'}
                aria-controls="chat-hub-my-students-section"
              >
                <span className="chat-hub-section-title">
                  My Students
                  {hasMyStudentUnread && <span className="chat-hub-section-unread-dot" aria-hidden="true" />}
                </span>
              </button>
              <div className="chat-hub-section-actions">
                <button
                  type="button"
                  className="chat-hub-section-action-btn"
                  onClick={() => toggleSidebarSection('my_students')}
                  title={expandedSidebarSection === 'my_students' ? 'Collapse My Students' : 'Expand My Students'}
                  aria-label={expandedSidebarSection === 'my_students' ? 'Collapse My Students' : 'Expand My Students'}
                >
                  <span className={`chat-hub-section-chevron ${expandedSidebarSection === 'my_students' ? 'expanded' : ''}`} aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.1">
                      <path d="M6 8l4 4 4-4" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
            <div
              id="chat-hub-my-students-section"
              className={`chat-hub-section-panel ${expandedSidebarSection === 'my_students' ? 'expanded' : 'collapsed'}`}
            >
              {renderSessionButtons(sortedMyStudentSessions)}
            </div>
          </div>

          <div className={`chat-hub-section ${expandedSidebarSection === 'ai' ? 'expanded' : 'collapsed'}`}>
            <div className="chat-hub-section-header">
              <button
                type="button"
                className="chat-hub-section-toggle"
                onClick={() => toggleSidebarSection('ai')}
                aria-expanded={expandedSidebarSection === 'ai'}
                aria-controls="chat-hub-ai-section"
              >
                <span className="chat-hub-section-title">AI</span>
              </button>
              <div className="chat-hub-section-actions">
                <button
                  type="button"
                  className="chat-hub-section-action-btn"
                  onClick={handleAiCreateClick}
                  title="New AI chat"
                  aria-label="New AI chat"
                  disabled={isCreatingAiSession}
                >
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.1">
                    <path d="M10 4v12M4 10h12" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="chat-hub-section-action-btn"
                  onClick={() => toggleSidebarSection('ai')}
                  title={expandedSidebarSection === 'ai' ? 'Collapse AI' : 'Expand AI'}
                  aria-label={expandedSidebarSection === 'ai' ? 'Collapse AI' : 'Expand AI'}
                >
                  <span className={`chat-hub-section-chevron ${expandedSidebarSection === 'ai' ? 'expanded' : ''}`} aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.1">
                      <path d="M6 8l4 4 4-4" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
            <div
              id="chat-hub-ai-section"
              className={`chat-hub-section-panel ${expandedSidebarSection === 'ai' ? 'expanded' : 'collapsed'}`}
            >
              <div className="chat-hub-session-list">
                {isAiCreatePanelOpen && (
                  <div className="chat-hub-ai-create-panel">
                    <div className="chat-hub-ai-create-title">New AI Chat</div>
                    <label className="chat-hub-ai-create-field">
                      <span className="chat-hub-ai-create-label">Session Language</span>
                      <ModernCombobox
                        options={aiSessionLanguageOptions}
                        value={aiSessionLanguage}
                        onChange={setAiSessionLanguage}
                        placeholder="Select session language"
                        disabled={isCreatingAiSession}
                        allowTyping={false}
                      />
                    </label>
                    <div className="chat-hub-ai-create-actions">
                      <button
                        type="button"
                        className="chat-hub-ai-create-btn subtle"
                        onClick={() => setIsAiCreatePanelOpen(false)}
                        disabled={isCreatingAiSession}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="chat-hub-ai-create-btn primary"
                        onClick={handleCreateAiSession}
                        disabled={isCreatingAiSession}
                      >
                        {isCreatingAiSession ? 'Creating...' : 'Create'}
                      </button>
                    </div>
                  </div>
                )}
                {sortedAiSessions.length > 0 ? (
                  sortedAiSessions.map((session) => (
                    <button
                      key={session.id}
                      type="button"
                      className={`chat-hub-ai-session-item ${session.id === activeSessionId ? 'active' : ''}`}
                      onClick={() => {
                        setActiveSession(session.id);
                        setMessages([]);
                        setAiCreateNotice('');
                      }}
                    >
                      <div className="chat-hub-session-main">
                        <div className="chat-hub-avatar chat-hub-avatar-sm">
                          <img
                            className="chat-hub-avatar-img"
                            src={resolveAvatarUrl(session.peerAvatar)}
                            alt={`${session.name} avatar`}
                            onError={(event) => {
                              event.currentTarget.classList.add('is-hidden');
                            }}
                          />
                        </div>
                        <div className="chat-hub-session-content">
                          <div className="chat-hub-session-top">
                            <span className="chat-hub-session-name-wrap">
                              <span className="chat-hub-session-name">{session.name}</span>
                            </span>
                            <div className="chat-hub-session-meta">
                              <span className="chat-hub-session-time">{formatTime(session.updatedAt)}</span>
                            </div>
                          </div>
                          <div className="chat-hub-session-bottom">
                            <span className="chat-hub-session-preview">
                              {session.preview || 'No messages yet'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="chat-hub-section-empty">
                    {aiCreateNotice || 'No AI chats yet.'}
                  </div>
                )}
              </div>
            </div>
          </div>

        </aside>

        <section className={`chat-hub-thread mode-${chatViewMode}`}>
          {!activeSession ? (
            // Empty state: no session selected
            <>
              <header className="chat-hub-thread-header">
                <div />
                <div className="chat-hub-thread-actions">
                  <button
                    type="button"
                    className="chat-hub-icon-btn"
                    onClick={() => setChatViewMode((prev) => (prev === 'docked' ? 'focus' : 'docked'))}
                    title={chatViewMode === 'docked' ? 'Expand chat' : 'Exit full size'}
                    aria-label={chatViewMode === 'docked' ? 'Expand chat' : 'Exit full size'}
                  >
                    {chatViewMode === 'docked' ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 3h6v6" />
                        <path d="M9 21H3v-6" />
                        <path d="M21 3l-7 7" />
                        <path d="M3 21l7-7" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M8 3H3v5" />
                        <path d="M16 21h5v-5" />
                        <path d="M3 8l6-6" />
                        <path d="M21 16l-6 6" />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className="chat-hub-icon-btn danger"
                    onClick={onClose}
                    title="Close chat"
                    aria-label="Close chat"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </header>
              <div className="chat-hub-empty-state">
                <div className="chat-hub-empty-icon">
                  <svg data-testid="geist-icon" height="48" stroke-linejoin="round" viewBox="0 0 16 16" width="48" style={{ color: 'currentcolor' }}>
                    <path fillRule="evenodd" clipRule="evenodd" d="M2.8914 10.4028L2.98327 10.6318C3.22909 11.2445 3.5 12.1045 3.5 13C3.5 13.3588 3.4564 13.7131 3.38773 14.0495C3.69637 13.9446 4.01409 13.8159 4.32918 13.6584C4.87888 13.3835 5.33961 13.0611 5.70994 12.7521L6.22471 12.3226L6.88809 12.4196C7.24851 12.4724 7.61994 12.5 8 12.5C11.7843 12.5 14.5 9.85569 14.5 7C14.5 4.14431 11.7843 1.5 8 1.5C4.21574 1.5 1.5 4.14431 1.5 7C1.5 8.18175 1.94229 9.29322 2.73103 10.2153L2.8914 10.4028ZM2.8135 15.7653C1.76096 16 1 16 1 16C1 16 1.43322 15.3097 1.72937 14.4367C1.88317 13.9834 2 13.4808 2 13C2 12.3826 1.80733 11.7292 1.59114 11.1903C0.591845 10.0221 0 8.57152 0 7C0 3.13401 3.58172 0 8 0C12.4183 0 16 3.13401 16 7C16 10.866 12.4183 14 8 14C7.54721 14 7.10321 13.9671 6.67094 13.9038C6.22579 14.2753 5.66881 14.6656 5 15C4.23366 15.3832 3.46733 15.6195 2.8135 15.7653Z" fill="currentColor" />
                  </svg>
                </div>
                <div className="chat-hub-empty-title">Select a chat</div>
                <div className="chat-hub-empty-subtitle">Select a conversation to start messaging.</div>
              </div>
            </>
          ) : (
            // Active session: full chat interface
            <>
              <header className="chat-hub-thread-header">
                <div className="chat-hub-thread-ident">
                  <div className="chat-hub-avatar chat-hub-avatar-md">
                    <img
                      className="chat-hub-avatar-img"
                      src={resolveAvatarUrl(activeSession?.peerAvatar)}
                      alt={`${activeSession?.name || 'User'} avatar`}
                      onError={(event) => {
                        event.currentTarget.classList.add('is-hidden');
                      }}
                    />
                  </div>
                  <div className="chat-hub-thread-title">
                    {isActiveAiSession ? (
                      <div className="chat-hub-ai-thread-heading">
                        {editingAiSessionTitle ? (
                          <input
                            type="text"
                            className="chat-hub-ai-thread-title-input"
                            value={aiSessionTitleInput}
                            onChange={(event) => setAiSessionTitleInput(event.target.value)}
                            onBlur={handleSubmitAiSessionTitle}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                void handleSubmitAiSessionTitle();
                              }
                              if (event.key === 'Escape') {
                                event.preventDefault();
                                handleCancelEditAiSessionTitle();
                              }
                            }}
                            maxLength={60}
                            autoFocus
                            disabled={isRenamingAiSession}
                          />
                        ) : (
                          <button
                            type="button"
                            className="chat-hub-ai-thread-title-button"
                            onClick={handleStartEditAiSessionTitle}
                            disabled={isRenamingAiSession}
                            title="Rename AI chat"
                            aria-label="Rename AI chat"
                          >
                            <span className="chat-hub-ai-thread-title">
                              {activeSession?.name || 'Select a chat'}
                            </span>
                          </button>
                        )}
                        <span className="chat-hub-ai-language-tag">
                          {activeSession?.languageLabel || 'General'}
                        </span>
                      </div>
                    ) : (
                      <div className="chat-hub-ai-thread-heading">
                        <div className="chat-hub-thread-user">
                          {activeSession?.name || 'Select a chat'}
                        </div>
                        {activeSession?.languageLabel && (
                          <span className="chat-hub-ai-language-tag">
                            {activeSession.languageLabel}
                          </span>
                        )}
                      </div>
                    )}
                    <div className={`${isActiveAiSession ? 'chat-hub-ai-thread-model' : 'chat-hub-thread-meta'} status-${threadMeta.status}`}>
                      {threadMeta.text}
                    </div>
                  </div>
                </div>
                <div className="chat-hub-thread-actions">
                  {isActiveAiSession && (
                    <button
                      type="button"
                      className="chat-hub-icon-btn delete"
                      onClick={handleOpenDeleteAiConfirm}
                      disabled={isDeletingAiSession}
                      title="Delete AI chat"
                      aria-label="Delete AI chat"
                    >
                      <TrashBinIcon />
                    </button>
                  )}
                  <button
                    type="button"
                    className="chat-hub-icon-btn"
                    onClick={() => setChatViewMode((prev) => (prev === 'docked' ? 'focus' : 'docked'))}
                    title={chatViewMode === 'docked' ? 'Expand chat' : 'Exit full size'}
                    aria-label={chatViewMode === 'docked' ? 'Expand chat' : 'Exit full size'}
                  >
                    {chatViewMode === 'docked' ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 3h6v6" />
                        <path d="M9 21H3v-6" />
                        <path d="M21 3l-7 7" />
                        <path d="M3 21l7-7" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M8 3H3v5" />
                        <path d="M16 21h5v-5" />
                        <path d="M3 8l6-6" />
                        <path d="M21 16l-6 6" />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className="chat-hub-icon-btn danger"
                    onClick={onClose}
                    title="Close chat"
                    aria-label="Close chat"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </header>

              <div className="chat-hub-messages" ref={messagesRef}>
                {typingText && <div className="chat-hub-typing-text">{typingText}</div>}
                {isMessagesLoading && messages.length === 0 ? (
                  <div className="chat-hub-empty">
                    <p>Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="chat-hub-empty">
                    <p>
                      {activeSession?.isDraft
                        ? 'Send your first message to create this session.'
                        : 'No messages yet.'}
                    </p>
                  </div>
                ) : (
                  messages.map((msg, index) => {
                    const previousMessage = index > 0 ? messages[index - 1] : null;
                    const shouldShowDateDivider = (
                      !previousMessage
                      || !isSameCalendarDay(new Date(previousMessage.at), new Date(msg.at))
                    );
                    const visibleMessageText = String(msg.text || '').trim();
                    const isSystemError = msg.kind === 'system_error';
                    const isEmptyAssistantPending = (
                      msg.role === 'other'
                      && msg.pending
                      && !visibleMessageText
                    );

                    return (
                      <React.Fragment key={msg.id}>
                        {shouldShowDateDivider && (
                          <div className="chat-hub-date-divider" aria-label={formatMessageDateDivider(msg.at)}>
                            <span className="chat-hub-date-divider-line" />
                            <span className="chat-hub-date-divider-label">{formatMessageDateDivider(msg.at)}</span>
                            <span className="chat-hub-date-divider-line" />
                          </div>
                        )}
                        <div className={`chat-hub-row ${isSystemError ? 'row-system' : msg.role === 'me' ? 'row-me' : 'row-other'}`}>
                          <div className={isSystemError ? 'chat-hub-system-error-card' : `chat-hub-bubble ${msg.role === 'me' ? 'bubble-me' : 'bubble-other'} ${isEmptyAssistantPending ? 'bubble-typing' : ''}`}>
                            {isSystemError ? (
                              <>
                                <div className="chat-hub-system-error-head">
                                  <span className="chat-hub-system-error-icon" aria-hidden="true">
                                    <svg data-testid="geist-icon" height="16" strokeLinejoin="round" viewBox="0 0 16 16" width="16">
                                      <path fillRule="evenodd" clipRule="evenodd" d="M8.55846 2H7.44148L1.88975 13.5H14.1102L8.55846 2ZM9.90929 1.34788C9.65902 0.829456 9.13413 0.5 8.55846 0.5H7.44148C6.86581 0.5 6.34092 0.829454 6.09065 1.34787L0.192608 13.5653C-0.127943 14.2293 0.355835 15 1.09316 15H14.9068C15.6441 15 16.1279 14.2293 15.8073 13.5653L9.90929 1.34788ZM8.74997 4.75V5.5V8V8.75H7.24997V8V5.5V4.75H8.74997ZM7.99997 12C8.55226 12 8.99997 11.5523 8.99997 11C8.99997 10.4477 8.55226 10 7.99997 10C7.44769 10 6.99997 10.4477 6.99997 11C6.99997 11.5523 7.44769 12 7.99997 12Z" fill="currentColor" />
                                    </svg>
                                  </span>
                                  <div className="chat-hub-system-error-copy">{visibleMessageText}</div>
                                </div>
                                <div className="chat-hub-system-error-actions">
                                  <button
                                    type="button"
                                    className="chat-hub-system-error-btn"
                                    onClick={() => {
                                      void handleRetryAiSystemError(msg);
                                    }}
                                  >
                                    Retry
                                  </button>
                                  <button
                                    type="button"
                                    className="chat-hub-system-error-btn"
                                    onClick={() => {
                                      void handleEditAiSystemError(msg);
                                    }}
                                  >
                                    Edit
                                  </button>
                                </div>
                              </>
                            ) : isEmptyAssistantPending ? (
                              <TypingIndicator label="AI is generating a reply" />
                            ) : (
                              <>
                                <div className="chat-hub-markdown">
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={markdownComponents}
                                  >
                                    {visibleMessageText}
                                  </ReactMarkdown>
                                </div>
                                <span className="chat-hub-bubble-time">{formatTime(msg.at)}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
              </div>

              <footer className={`chat-hub-composer ${isActiveAiSession ? 'chat-hub-ai-composer' : ''}`}>
                {isActiveAiSession ? (
                  <>
                    <div className="chat-hub-ai-composer-surface">
                      {activeAiBlockingWarning && (
                        <div className="chat-hub-ai-warning-banner" role="status" aria-live="polite">
                          <span className="chat-hub-ai-warning-banner-icon" aria-hidden="true">
                            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M10 6.5v4.5" />
                              <path d="M10 14h.01" />
                              <path d="M8.68 3.84 1.92 15.5a1 1 0 0 0 .87 1.5h14.42a1 1 0 0 0 .87-1.5L11.32 3.84a1.52 1.52 0 0 0-2.64 0Z" />
                            </svg>
                          </span>
                          <span className="chat-hub-ai-warning-banner-copy">
                            {activeAiBlockingWarning.text}
                          </span>
                        </div>
                      )}
                      <textarea
                        ref={composerRef}
                        className="chat-hub-input chat-hub-ai-input"
                        placeholder={activeAiBlockingWarning ? 'Update your API key in Profile > API Keys to continue.' : 'Ask anything, refine an idea, or start a new thread...'}
                        value={composerText}
                        onChange={handleComposerChange}
                        onKeyDown={handleComposerKeyDown}
                        rows={1}
                      />
                      <div className="chat-hub-ai-composer-bar">
                        <div className="chat-hub-ai-composer-left">
                          <button
                            type="button"
                            className="chat-hub-attach-btn chat-hub-ai-bar-btn"
                            title="Attach files"
                            aria-label="Attach files"
                            disabled
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                              <path d="M12 5v14M5 12h14" />
                            </svg>
                          </button>
                        </div>
                        <div className="chat-hub-ai-composer-right">
                          <div className="chat-hub-ai-model-menu" ref={aiModelMenuRef}>
                            <button
                              type="button"
                              className="chat-hub-ai-model-btn"
                              title="Select model"
                              aria-label="Select model"
                              aria-expanded={isAiModelMenuOpen}
                              onClick={() => setIsAiModelMenuOpen((prev) => !prev)}
                              disabled={aiModels.length === 0}
                            >
                              <span className="chat-hub-ai-model-btn-copy">
                                <span className="chat-hub-ai-model-btn-label">
                                  {activeSession?.currentModelName || 'Select model'}
                                </span>
                              </span>
                              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M6 8l4 4 4-4" />
                              </svg>
                            </button>
                            {isAiModelMenuOpen && aiModels.length > 0 && (
                              <div className="chat-hub-ai-model-dropdown">
                                {aiModels.map((model) => {
                                  const isSelected = model.modelId === (activeSession?.currentModelId || '');
                                  const previewAvatar = resolveAvatarUrl(model.icon);
                                  return (
                                    <button
                                      key={model.modelId}
                                      type="button"
                                      className={`chat-hub-ai-model-option ${isSelected ? 'selected' : ''}`}
                                      onClick={() => handleSelectAiModel(model)}
                                    >
                                      <span className="chat-hub-ai-model-option-avatar">
                                        <img
                                          className="chat-hub-avatar-img"
                                          src={previewAvatar}
                                          alt={`${model.displayName} avatar`}
                                          onError={(event) => {
                                            event.currentTarget.classList.add('is-hidden');
                                          }}
                                        />
                                      </span>
                                      <span className="chat-hub-ai-model-option-copy">
                                        <span className="chat-hub-ai-model-option-name">{model.displayName}</span>
                                        <span className="chat-hub-ai-model-option-meta">
                                          <span className="chat-hub-ai-model-option-provider">{model.provider || 'AI model'}</span>
                                          <span className="chat-hub-ai-model-option-id">{model.modelId}</span>
                                        </span>
                                      </span>
                                      {isSelected && (
                                        <span className="chat-hub-ai-model-option-check" aria-hidden="true">
                                          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2">
                                            <path d="M4.5 10.5l3.2 3.2 7.8-8.2" />
                                          </svg>
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            className="chat-hub-send-btn chat-hub-ai-send-btn"
                            onClick={handleSend}
                            disabled={!composerText.trim() || !activeSession?.currentModelId || isAiSending || Boolean(activeAiBlockingWarning)}
                            title="Send message"
                            aria-label="Send message"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <line x1="12" y1="19" x2="12" y2="5" />
                              <polyline points="5 12 12 5 19 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="chat-hub-attach-btn"
                      title="Attach files"
                      aria-label="Attach files"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </button>
                    <textarea
                      ref={composerRef}
                      className="chat-hub-input"
                      placeholder="Type a message..."
                      value={composerText}
                      onChange={handleComposerChange}
                      onKeyDown={handleComposerKeyDown}
                      rows={1}
                    />
                    <button
                      type="button"
                      className="chat-hub-send-btn"
                      onClick={handleSend}
                      disabled={!composerText.trim() || isCreatingSession}
                      title="Send message"
                      aria-label="Send message"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="19" x2="12" y2="5" />
                        <polyline points="5 12 12 5 19 12" />
                      </svg>
                    </button>
                  </>
                )}
              </footer>
            </>
          )}
        </section>
      </div>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={() => confirmDialog.onConfirm?.()}
        onCancel={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
        isPending={isDeletingAiSession}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
}
